/**
 * Capital Expenditure Main Component
 * नयाँ डाटा थप्नुहोस् (Add New Data) - Complete Design with Table and All Elements
 * 
 * This component provides a comprehensive capital expenditure management system with:
 * - Complete table structure for all quarters (Q1, Q2, Q3, Q4)
 * - Capital and operational expenditure sections
 * - Form controls and validation
 * - Data management and CRUD operations
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import InstitutionManager from '../institutionManager/institutionManager';
import styles from './capitalExpenditure.module.scss';
import CapitalExpenditureHeader from './components/CapitalExpenditureHeader';
import CapitalExpenditureForm from './components/CapitalExpenditureForm';
import CapitalExpenditureDataView from './components/CapitalExpenditureDataView';
import Settings from './components/Settings';
import CapitalExpenditureEditForm from './editForm';
import {
  emptyIncomeTotals,
  incomePayloadFromState,
  incomeStateFromApi
} from './components/IncomeTotalRow';

import { parseNumericValue, formatCalculationResult } from './utils/numberUtils';
import { exportToExcel, exportFormDataToExcel, exportOrganizedToExcel } from './utils/excelExport';
import { pickCurrentFiscalYear, keepOrDefaultFiscalYear } from '../budget/fiscalYearUtils';
import {
  buildFixedBudgetTemplateRows,
  inferBudgetItemTypeFromCode,
  sortBudgetRowsByCode as sortBudgetRowsByFixedCode,
  toCanonicalBudgetCode
} from '../budget/budgetTemplate';

// API Configuration - Auto-detect environment and set appropriate API URL
const getApiBaseUrl = () => {
  // Check if we're in development (localhost) or production
  const isLocalhost = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' ||
                     window.location.hostname.includes('localhost');
  
  // Check if we're on the production domain
  const isProduction = window.location.hostname.includes('cloudlab.com.np') ||
                      window.location.hostname.includes('muaccount');
  
  // Priority: localStorage > auto-detection > default
  const savedUrl = localStorage.getItem('apiBaseUrl');
  
  if (savedUrl) {
    return savedUrl;
  }
  
  if (isLocalhost) {
    return 'http://localhost:8000';
  }
  
  if (isProduction) {
    return 'https://muaccount.api.cloudlab.com.np';
  }
  
  // Default fallback
  return 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();
const CAPITAL_EXPENDITURE_API = `${API_BASE_URL}/capital-expenditure`;
const INSTITUTIONS_API = `${API_BASE_URL}/institutions`;
const FISCAL_YEARS_API = `${API_BASE_URL}/fiscal-years`;
const EXPENDITURE_ITEMS_API = `${API_BASE_URL}/expenditure-items`;
const GET_EXPENDITURE_API = `${API_BASE_URL}/get-expenditure-by-fiscal-year`;
const BULK_CREATE_IMPROVED_API = `${API_BASE_URL}/capital-expenditure/bulk-create-improved/`;
const UPLOAD_ITEMS_API = `${API_BASE_URL}/upload-expenditure-items/`;
const OFFICES_API = `${API_BASE_URL}/offices`;
const MASTER_ITEMS_API = `${API_BASE_URL}/master-expenditure-items`;
const BUDGET_LIST_API = `${API_BASE_URL}/budgets/`;
const SAVE_BUDGET_API = `${API_BASE_URL}/budgets/bulk-create/`;
const TOKEN_SEARCH_API = `${API_BASE_URL}/token/search/`;

const mapTokenSearchToEditForm = (tokenData, fallbackToken = '') => ({
  ...tokenData.capital_expenditure,
  opened_current_year: tokenData.opened_current_year,
  source_fiscal_year_display: tokenData.source_fiscal_year_display,
  working_fiscal_year_display: tokenData.working_fiscal_year_display,
  source_token: tokenData.source_token || fallbackToken,
  available_fiscal_years: Array.isArray(tokenData.available_fiscal_years)
    ? tokenData.available_fiscal_years
    : [],
});

// Helper function to get authentication headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  console.log('getAuthHeaders called, token:', token ? token.substring(0, 20) + '...' : 'null');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Token ${token}` })
  };
};

/**
 * Main Component: नयाँ डाटा थप्नुहोस् (Add New Data)
 * Complete design with comprehensive table structure and all functionality
 */

const blankIfZero = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(num) && num === 0 ? '' : String(value);
};

const CapitalExpenditureMain = ({ activeTab = 'newData', onLogout, isAuthenticated }) => {
  // React Router hooks
  const navigate = useNavigate();
  const location = useLocation();
  
  // Core state - Initialize with proper structure for नयाँ डाटा थप्नुहोस्
  const [rows, setRows] = useState(Array(10).fill().map((_, index) => ({
    id: index + 1,
    codeNumber: '', 
    expenseTitle: '', 
    unallocatedBudget: '', 
    revisedBudget: '',
    q1BudgetDemand: '', 
    q1BudgetExpenditure: '', 
    q1AuthorityReceived: '', 
    q1Expense: '', 
    q1RemainingBalance: '0',
    q2BudgetDemand: '', 
    q2BudgetExpenditure: '', 
    q2AuthorityReceived: '', 
    q2Expense: '', 
    q2RemainingBalance: '0',
    q3BudgetDemand: '', 
    q3BudgetExpenditure: '', 
    q3AuthorityReceived: '', 
    q3Expense: '', 
    q3RemainingBalance: '0',
    q4BudgetDemand: '', 
    q4BudgetExpenditure: '', 
    q4AuthorityReceived: '', 
    q4Expense: '', 
    q4RemainingBalance: '0',
    totalBudgetDemand: '',
    totalBudgetExpenditure: '',
    totalAuthorityReceived: '',
    totalExpense: '',
    totalRemainingBalance: '',
    is_uploaded: false,
    isNewlyAdded: false
  })));

  const [operationalRows, setOperationalRows] = useState(Array(10).fill().map((_, index) => ({
    id: `op_${index + 1}`,
    codeNumber: '', 
    expenseTitle: '', 
    unallocatedBudget: '', 
    revisedBudget: '',
    q1BudgetDemand: '', 
    q1BudgetExpenditure: '', 
    q1AuthorityReceived: '', 
    q1Expense: '', 
    q1RemainingBalance: '0',
    q2BudgetDemand: '', 
    q2BudgetExpenditure: '', 
    q2AuthorityReceived: '', 
    q2Expense: '', 
    q2RemainingBalance: '0',
    q3BudgetDemand: '', 
    q3BudgetExpenditure: '', 
    q3AuthorityReceived: '', 
    q3Expense: '', 
    q3RemainingBalance: '0',
    q4BudgetDemand: '', 
    q4BudgetExpenditure: '', 
    q4AuthorityReceived: '', 
    q4Expense: '', 
    q4RemainingBalance: '0',
    totalBudgetDemand: '',
    totalBudgetExpenditure: '',
    totalAuthorityReceived: '',
    totalExpense: '',
    totalRemainingBalance: '',
    isNewlyAdded: false
  })));

  const [incomeTotals, setIncomeTotals] = useState(emptyIncomeTotals());

  const handleIncomeChange = (field, value) => {
    setIncomeTotals(prev => ({ ...prev, [field]: value }));
  };

  // UI state - Use prop if provided, otherwise use internal state
  const [internalActiveTab, setInternalActiveTab] = useState('capital'); // Default to capital tab (नयाँ डाटा थप्नुहोस्)
  const currentActiveTab = activeTab || internalActiveTab;
  const setActiveTab = (tab) => {
    setInternalActiveTab(tab);
    // Navigate to appropriate route based on tab
    if (tab === 'capital') {
      navigate('/capital-expenditure/new-data');
    } else if (tab === 'budget') {
      navigate('/budjet');
    } else if (tab === 'dataView') {
      navigate('/capital-expenditure/data-view');
    } else if (tab === 'institution') {
      navigate('/capital-expenditure/institution-manager');
    } else if (tab === 'settings') {
      navigate('/capital-expenditure/settings');
    }
  };
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Data state
  const [selectedOffice, setSelectedOffice] = useState({ id: null, name: '' });
  const [existingData, setExistingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [viewMode, setViewMode] = useState(false);
  const [selectedDataForEdit, setSelectedDataForEdit] = useState(null);
  const [lastSavedToken, setLastSavedToken] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [cameFromTokenSearch, setCameFromTokenSearch] = useState(false);
  const [tokenYearSwitchLoading, setTokenYearSwitchLoading] = useState(false);

  
  // Office dropdown state
  const [showOfficeDropdown, setShowOfficeDropdown] = useState(false);
  const [officeSearchTerm, setOfficeSearchTerm] = useState('');
  const [officeOptions, setOfficeOptions] = useState([]);
  
  // Fiscal year state
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  
  // Message state
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState('');
  const [showTokenPopup, setShowTokenPopup] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Refresh popup state
  const [showRefreshPopup, setShowRefreshPopup] = useState(false);
  const [hasReturnedFromOtherPage, setHasReturnedFromOtherPage] = useState(false);
  
  // Filters state
  const [filters, setFilters] = useState({
    institution: '', fiscalYear: '', dateFrom: '', dateTo: '', itemType: '', minBudget: '', maxBudget: '', status: ''
  });

  // Master items state for auto-population
  const [masterItems, setMasterItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Load master items only on the new-data form, not on data-view/settings
  useEffect(() => {
    if (currentActiveTab === 'newData' || currentActiveTab === 'capital') {
      loadMasterItems();
    }
  }, [currentActiveTab]);

  // Handle navigation state from token search
  useEffect(() => {
    if (location.state?.fromTokenSearch && location.state?.tokenData) {
      const { tokenData, accessType } = location.state;
      
      // Set the data for editing/viewing
      setEditFormData(mapTokenSearchToEditForm(tokenData));
      // Set view mode based on access type
      setViewMode(accessType === 'view');
      // Show the edit form
      setShowEditForm(true);
      // Set flag to indicate edit form was opened via token search
      setCameFromTokenSearch(true);
      
      // Clear the navigation state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });

      try {
        sessionStorage.removeItem('allowTokenAccess');
      } catch (e) {}
    }
  }, [location.state, navigate]);

  // Ensure temporary token access is cleared on unmount
  useEffect(() => {
    return () => {
      try {
        sessionStorage.removeItem('allowTokenAccess');
      } catch (e) {}
    };
  }, []);

  // Function to load master expenditure items
  const loadMasterItems = async () => {
    try {
      const response = await fetch(`${MASTER_ITEMS_API}/`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setMasterItems(result.data);
          
          // Check if we already have master items loaded
          const hasMasterItems = rows.some(row => row.is_uploaded) || operationalRows.some(row => row.is_uploaded);
          
          if (hasMasterItems) {
            // Show message that items are already loaded
            const capitalCount = result.data.filter(item => item.item_type === 'capital').length;
            const operationalCount = result.data.filter(item => item.item_type === 'operational').length;
            setMessage(`मास्टर सूचीहरू पहिले नै लोड गरिएको छ। कुल ${capitalCount} पुँजीगत खर्च र ${operationalCount} संचालन खर्च सूचीहरू उपलब्ध छन्।`);
            setMessageType('info');
          } else {
            // Populate form with master items by default
            populateFormWithMasterItems(result.data);
            
            // Show success message
            const capitalCount = result.data.filter(item => item.item_type === 'capital').length;
            const operationalCount = result.data.filter(item => item.item_type === 'operational').length;
            setMessage(`सफलतापूर्वक ${capitalCount} पुँजीगत खर्च र ${operationalCount} संचालन खर्च सूचीहरू लोड गरियो।`);
            setMessageType('success');
          }
        }
      }
    } catch (error) {
      console.error('Error loading master items:', error);
      setMessage('मास्टर सूचीहरू लोड गर्न असफल।');
      setMessageType('error');
    }
  };

  // Function to handle refresh button click
  const handleRefreshClick = () => {
    if (hasReturnedFromOtherPage) {
      // Show refresh popup first time
      setShowRefreshPopup(true);
    } else {
      // Direct refresh if not returning from other page
      window.location.reload();
    }
  };

  // Function to confirm refresh
  const confirmRefresh = () => {
    setShowRefreshPopup(false);
    setHasReturnedFromOtherPage(false);
    window.location.reload();
  };

  // Function to cancel refresh
  const cancelRefresh = () => {
    setShowRefreshPopup(false);
    setHasReturnedFromOtherPage(false);
  };

  // Function to force refresh master items (for manual refresh button)
  const forceRefreshMasterItems = async () => {
    console.log('Force refreshing master items...');
    try {
      setLoading(true);
      const response = await fetch(`${MASTER_ITEMS_API}/`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          console.log('Master items loaded successfully:', result.data.length, 'items');
          setMasterItems(result.data);
          // Always populate form with fresh master items
          // Add a small delay to prevent state conflicts
          setTimeout(() => {
            populateFormWithMasterItems(result.data);
          }, 100);
          
          // Show success message
          const capitalCount = result.data.filter(item => item.item_type === 'capital').length;
          const operationalCount = result.data.filter(item => item.item_type === 'operational').length;
          setMessage(`सफलतापूर्वक ${capitalCount} पुँजीगत खर्च र ${operationalCount} संचालन खर्च सूचीहरू रिफ्रेश गरियो।`);
          setMessageType('success');
        }
      }
    } catch (error) {
      console.error('Error refreshing master items:', error);
      setMessage('मास्टर सूचीहरू रिफ्रेश गर्न असफल।');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // Function to populate form with master items
  const populateFormWithMasterItems = (items) => {
    console.log('Populating form with master items:', items.length, 'items');
    const capitalItems = items.filter(item => item.item_type === 'capital');
    const operationalItems = items.filter(item => item.item_type === 'operational');

    console.log('Capital items:', capitalItems.length, 'Operational items:', operationalItems.length);

    // Only populate if we don't have any master items already loaded
    if (rows.length === 0 || rows.every(row => !row.is_uploaded)) {
      console.log('No existing master items, populating form...');
      // Ensure we have enough rows for all items
      ensureEnoughRows(items);

      // Populate capital rows - create rows for ALL capital items
      if (capitalItems.length > 0) {
        const newCapitalRows = capitalItems.map((item, index) => ({
          id: index + 1,
          codeNumber: item.code_number || '',
          expenseTitle: item.expense_title || '',
          unallocatedBudget: '',
          revisedBudget: '',
          q1BudgetDemand: '',
          q1BudgetExpenditure: '',
          q1AuthorityReceived: '',
          q1Expense: '',
          q1RemainingBalance: '0',
          q2BudgetDemand: '',
          q2BudgetExpenditure: '',
          q2AuthorityReceived: '',
          q2Expense: '',
          q2RemainingBalance: '0',
          q3BudgetDemand: '',
          q3BudgetExpenditure: '',
          q3AuthorityReceived: '',
          q3Expense: '',
          q3RemainingBalance: '0',
          q4BudgetDemand: '',
          q4BudgetExpenditure: '',
          q4AuthorityReceived: '',
          q4Expense: '',
          q4RemainingBalance: '0',
          totalBudgetDemand: '',
          totalBudgetExpenditure: '',
          totalAuthorityReceived: '',
          totalExpense: '',
          totalRemainingBalance: '',
          is_uploaded: true,
          isNewlyAdded: false
        }));
        setRows(newCapitalRows);
        
        // Add some extra empty rows for manual entry
        if (capitalItems.length < 15) {
          const extraRows = Array(15 - capitalItems.length).fill().map((_, index) => ({
            id: capitalItems.length + index + 1,
            codeNumber: '', 
            expenseTitle: '', 
            unallocatedBudget: '', 
            revisedBudget: '',
            q1BudgetDemand: '', 
            q1BudgetExpenditure: '', 
            q1AuthorityReceived: '', 
            q1Expense: '', 
            q1RemainingBalance: '0',
            q2BudgetDemand: '', 
            q2BudgetExpenditure: '', 
            q2AuthorityReceived: '', 
            q2Expense: '', 
            q2RemainingBalance: '0',
            q3BudgetDemand: '', 
            q3BudgetExpenditure: '', 
            q3AuthorityReceived: '', 
            q3Expense: '', 
            q3RemainingBalance: '0',
            q4BudgetDemand: '', 
            q4BudgetExpenditure: '', 
            q4AuthorityReceived: '', 
            q4Expense: '', 
            q4RemainingBalance: '0',
            totalBudgetDemand: '',
            totalBudgetExpenditure: '',
            totalAuthorityReceived: '',
            totalExpense: '',
            totalRemainingBalance: '',
            is_uploaded: false,
            isNewlyAdded: false
          }));
          setRows(prevRows => [...prevRows, ...extraRows]);
        }
    }

      // Populate operational rows - create rows for ALL operational items
      if (operationalItems.length > 0) {
        const newOperationalRows = operationalItems.map((item, index) => ({
          id: `op_${index + 1}`,
          codeNumber: item.code_number || '',
          expenseTitle: item.expense_title || '',
          unallocatedBudget: '',
          revisedBudget: '',
          q1BudgetDemand: '',
          q1BudgetExpenditure: '',
          q1AuthorityReceived: '',
          q1Expense: '',
          q1RemainingBalance: '0',
          q2BudgetDemand: '',
          q2BudgetExpenditure: '',
          q2AuthorityReceived: '',
          q2Expense: '',
          q2RemainingBalance: '0',
          q3BudgetDemand: '',
          q3BudgetExpenditure: '',
          q3AuthorityReceived: '',
          q3Expense: '',
          q3RemainingBalance: '0',
          q4BudgetDemand: '',
          q4BudgetExpenditure: '',
          q4AuthorityReceived: '',
          q4Expense: '',
          q4RemainingBalance: '0',
          totalBudgetDemand: '',
          totalBudgetExpenditure: '',
          totalAuthorityReceived: '',
          totalExpense: '',
          totalRemainingBalance: '',
          is_uploaded: true,
          isNewlyAdded: false
        }));
        setOperationalRows(newOperationalRows);
        
        // Add some extra empty rows for manual entry
        if (operationalItems.length < 15) {
          const extraRows = Array(15 - operationalItems.length).fill().map((_, index) => ({
            id: `op_${operationalItems.length + index + 1}`,
            codeNumber: '', 
            expenseTitle: '', 
            unallocatedBudget: '', 
            revisedBudget: '',
            q1BudgetDemand: '', 
            q1BudgetExpenditure: '', 
            q1AuthorityReceived: '', 
            q1Expense: '', 
            q1RemainingBalance: '0',
            q2BudgetDemand: '', 
            q2BudgetExpenditure: '', 
            q2AuthorityReceived: '', 
            q2Expense: '', 
            q2RemainingBalance: '0',
            q3BudgetDemand: '', 
            q3BudgetExpenditure: '', 
            q3AuthorityReceived: '', 
            q3Expense: '', 
            q3RemainingBalance: '0',
            q4BudgetDemand: '', 
            q4BudgetExpenditure: '', 
            q4AuthorityReceived: '', 
            q4Expense: '', 
            q4RemainingBalance: '0',
            totalBudgetDemand: '',
            totalBudgetExpenditure: '',
            totalAuthorityReceived: '',
            totalExpense: '',
            totalRemainingBalance: '',
            is_uploaded: false,
            isNewlyAdded: false
          }));
          setOperationalRows(prevRows => [...prevRows, ...extraRows]);
        }
      }
    }
  };

  // Event handlers
  const handleInputChange = (rowId, field, value) => {
    // Debug logging for q1BudgetDemand field
    if (field === 'q1BudgetDemand') {
      console.log('handleInputChange - Field:', field);
      console.log('handleInputChange - Received value:', value);
      console.log('handleInputChange - Value type:', typeof value);
      console.log('handleInputChange - Value length:', value ? value.length : 0);
    }
    
    setRows(prevRows => {
      const updatedRows = prevRows.map(row => 
        row.id === rowId ? { ...row, [field]: value } : row
      );
      
      // Auto-calculate remaining balance immediately
      const quarterMatch = field.match(/^q(\d)(AuthorityReceived|Expense)$/);
      if (quarterMatch) {
        const quarter = quarterMatch[1];
        const authorityField = `q${quarter}AuthorityReceived`;
        const expenseField = `q${quarter}Expense`;
        const remainingField = `q${quarter}RemainingBalance`;
        
        const updatedRow = updatedRows.find(row => row.id === rowId);
        const authority = parseNumericValue(updatedRow[authorityField] || '0');
        const expense = parseNumericValue(updatedRow[expenseField] || '0');
        const remaining = Math.max(0, authority - expense);
        
        // Format the remaining balance according to user's preferred digit format
        const formattedRemaining = formatCalculationResult(remaining, updatedRow);
        
        return updatedRows.map(row => 
          row.id === rowId ? { ...row, [remainingField]: formattedRemaining } : row
        );
      }
      
      // Also recalculate all remaining balances for the row to ensure format consistency
      const updatedRow = updatedRows.find(row => row.id === rowId);
      if (updatedRow) {
        const recalculatedRows = updatedRows.map(row => {
          if (row.id === rowId) {
            // Recalculate all quarter remaining balances
            const quarters = [1, 2, 3, 4];
            const recalculatedRow = { ...row };
            
            quarters.forEach(quarter => {
              const authorityField = `q${quarter}AuthorityReceived`;
              const expenseField = `q${quarter}Expense`;
              const remainingField = `q${quarter}RemainingBalance`;
              
              const authority = parseNumericValue(row[authorityField] || '0');
              const expense = parseNumericValue(row[expenseField] || '0');
              const remaining = Math.max(0, authority - expense);
              
              // Format according to the new detected format
              const formattedRemaining = formatCalculationResult(remaining, recalculatedRow);
              recalculatedRow[remainingField] = formattedRemaining;
            });
            
            return recalculatedRow;
          }
          return row;
        });
        
        return recalculatedRows;
      }
      
      return updatedRows;
    });
  };
  
  // Immediate calculation function for real-time updates
  const calculateRemainingBalance = (row, quarter) => {
    const authorityField = `q${quarter}AuthorityReceived`;
    const expenseField = `q${quarter}Expense`;
    const authority = parseNumericValue(row[authorityField] || '0');
    const expense = parseNumericValue(row[expenseField] || '0');
    const remaining = Math.max(0, authority - expense);
    
    return formatCalculationResult(remaining, row);
  };

  // Handle operational rows input change
  const handleOperationalInputChange = (rowId, field, value) => {
    setOperationalRows(prevRows => {
      const updatedRows = prevRows.map(row => 
        row.id === rowId ? { ...row, [field]: value } : row
      );
      
      // Auto-calculate remaining balance immediately
      const quarterMatch = field.match(/^q(\d)(AuthorityReceived|Expense)$/);
      if (quarterMatch) {
        const quarter = quarterMatch[1];
        const authorityField = `q${quarter}AuthorityReceived`;
        const expenseField = `q${quarter}Expense`;
        const remainingField = `q${quarter}RemainingBalance`;
        
        const updatedRow = updatedRows.find(row => row.id === rowId);
        const authority = parseNumericValue(updatedRow[authorityField] || '0');
        const expense = parseNumericValue(updatedRow[expenseField] || '0');
        const remaining = Math.max(0, authority - expense);
        
        // Format the remaining balance according to user's preferred digit format
        const formattedRemaining = formatCalculationResult(remaining, updatedRow);
        
        return updatedRows.map(row => 
          row.id === rowId ? { ...row, [remainingField]: formattedRemaining } : row
        );
      }
      
      // Also recalculate all remaining balances for the row to ensure format consistency
      const updatedRow = updatedRows.find(row => row.id === rowId);
      if (updatedRow) {
        const recalculatedRows = updatedRows.map(row => {
          if (row.id === rowId) {
            // Recalculate all quarter remaining balances
            const quarters = [1, 2, 3, 4];
            const recalculatedRow = { ...row };
            
            quarters.forEach(quarter => {
              const authorityField = `q${quarter}AuthorityReceived`;
              const expenseField = `q${quarter}Expense`;
              const remainingField = `q${quarter}RemainingBalance`;
              
              const authority = parseNumericValue(row[authorityField] || '0');
              const expense = parseNumericValue(row[expenseField] || '0');
              const remaining = Math.max(0, authority - expense);
              
              // Format according to the new detected format
              const formattedRemaining = formatCalculationResult(remaining, recalculatedRow);
              recalculatedRow[remainingField] = formattedRemaining;
            });
            
            return recalculatedRow;
          }
          return row;
        });
        
        return recalculatedRows;
      }
      
      return updatedRows;
    });
  };

  // Function to check if a field should be read-only based on master items
  const isFieldReadOnly = (rowId, field) => {
    const row = rows.find(r => r.id === rowId) || operationalRows.find(r => r.id === rowId);
    if (!row || !row.is_uploaded) return false;
    
    // Make code number and expense title read-only for uploaded items
    if (field === 'codeNumber' || field === 'expenseTitle') {
      return true;
    }
    
    return false;
  };

  // Function to add multiple rows at once for master items
  const addMultipleRows = (count, type = 'capital') => {
    if (type === 'capital') {
      setRows(prevRows => {
        const newRows = Array(count).fill().map((_, index) => ({
          id: prevRows.length > 0 ? Math.max(...prevRows.map(row => row.id)) + index + 1 : index + 1,
          codeNumber: '', 
          expenseTitle: '', 
          unallocatedBudget: '', 
          revisedBudget: '',
          q1BudgetDemand: '', 
          q1BudgetExpenditure: '', 
          q1AuthorityReceived: '', 
          q1Expense: '', 
          q1RemainingBalance: '0',
          q2BudgetDemand: '', 
          q2BudgetExpenditure: '', 
          q2AuthorityReceived: '', 
          q2Expense: '', 
          q2RemainingBalance: '0',
          q3BudgetDemand: '', 
          q3BudgetExpenditure: '', 
          q3AuthorityReceived: '', 
          q3Expense: '', 
          q3RemainingBalance: '0',
          q4BudgetDemand: '', 
          q4BudgetExpenditure: '', 
          q4AuthorityReceived: '', 
          q4Expense: '', 
          q4RemainingBalance: '0',
          totalBudgetDemand: '',
          totalBudgetExpenditure: '',
          totalAuthorityReceived: '',
          totalExpense: '',
          totalRemainingBalance: '',
          is_uploaded: false,
          isNewlyAdded: false
        }));
        return [...prevRows, ...newRows];
      });
    } else {
      setOperationalRows(prevRows => {
        const newRows = Array(count).fill().map((_, index) => ({
          id: `op_${Date.now()}_${index}`,
          codeNumber: '', 
          expenseTitle: '', 
          unallocatedBudget: '', 
          revisedBudget: '',
          q1BudgetDemand: '', 
          q1BudgetExpenditure: '', 
          q1AuthorityReceived: '', 
          q1Expense: '', 
          q1RemainingBalance: '0',
          q2BudgetDemand: '', 
          q2BudgetExpenditure: '', 
          q2AuthorityReceived: '', 
          q2Expense: '', 
          q2RemainingBalance: '0',
          q3BudgetDemand: '', 
          q3BudgetExpenditure: '', 
          q3AuthorityReceived: '', 
          q3Expense: '', 
          q3RemainingBalance: '0',
          q4BudgetDemand: '', 
          q4BudgetExpenditure: '', 
          q4AuthorityReceived: '', 
          q4Expense: '', 
          q4RemainingBalance: '0',
          totalBudgetDemand: '',
          totalBudgetExpenditure: '',
          totalAuthorityReceived: '',
          totalExpense: '',
          totalRemainingBalance: '',
          is_uploaded: false,
          isNewlyAdded: false
        }));
        return [...prevRows, ...newRows];
      });
    }
  };

  // Function to ensure enough rows exist for all master items
  const ensureEnoughRows = (items) => {
    const capitalItems = items.filter(item => item.item_type === 'capital');
    const operationalItems = items.filter(item => item.item_type === 'operational');
    
    // Ensure we have enough capital rows
    if (rows.length < capitalItems.length) {
      const neededRows = capitalItems.length - rows.length;
      addMultipleRows(neededRows, 'capital');
    }
    
    // Ensure we have enough operational rows
    if (operationalRows.length < operationalItems.length) {
      const neededRows = operationalItems.length - operationalRows.length;
      addMultipleRows(neededRows, 'operational');
    }
  };

  const handleUploadItems = async (file) => {
    if (!file) {
      return;
    }
    
    // Allow upload without office/fiscal year selection

    setUploading(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('capital_expenditure_id', 'new'); // For new data entry
      formData.append('office_id', selectedOffice?.id || '');
      formData.append('fiscal_year_id', selectedFiscalYear?.id || '');

      const response = await fetch(UPLOAD_ITEMS_API, {
        method: 'POST',
        headers: {
          'Authorization': getAuthHeaders().Authorization
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      // Debug: Log the response to see what we're getting
      console.log('Upload response:', result);
      console.log('Response keys:', Object.keys(result));
      console.log('Success value:', result.success);
      console.log('Status value:', result.status);
      
      if (result.success) {
        // Update the rows with uploaded data
        if (result.capital_items && result.capital_items.length > 0) {
          // Create new rows for uploaded items, preserving existing rows
          const newCapitalRows = result.capital_items.map((item, index) => ({
            id: `uploaded_cap_${Date.now()}_${index}`,
            codeNumber: item.code_number || '',
            expenseTitle: item.expense_title || '',
            unallocatedBudget: '',
            revisedBudget: '',
            q1BudgetDemand: '',
            q1BudgetExpenditure: '',
            q1AuthorityReceived: '',
            q1Expense: '',
            q1RemainingBalance: '0',
            q2BudgetDemand: '',
            q2BudgetExpenditure: '',
            q2AuthorityReceived: '',
            q2Expense: '',
            q2RemainingBalance: '0',
            q3BudgetDemand: '',
            q3BudgetExpenditure: '',
            q3AuthorityReceived: '',
            q3Expense: '',
            q3RemainingBalance: '0',
            q4BudgetDemand: '',
            q4BudgetExpenditure: '',
            q4AuthorityReceived: '',
            q4Expense: '',
            q4RemainingBalance: '0',
            totalBudgetDemand: '',
            totalBudgetExpenditure: '',
            totalAuthorityReceived: '',
            totalExpense: '',
            totalRemainingBalance: '',
            is_uploaded: true
          }));
          
          // Add uploaded items to existing rows
          setRows(prevRows => {
            // Remove any previously uploaded items
            const existingRows = prevRows.filter(row => !row.is_uploaded);
            return [...existingRows, ...newCapitalRows];
          });
        }

        if (result.operational_items && result.operational_items.length > 0) {
          // Create new rows for uploaded operational items
          const newOperationalRows = result.operational_items.map((item, index) => ({
            id: `uploaded_op_${Date.now()}_${index}`,
            codeNumber: item.code_number || '',
            expenseTitle: item.expense_title || '',
            unallocatedBudget: '',
            revisedBudget: '',
            q1BudgetDemand: '',
            q1BudgetExpenditure: '',
            q1AuthorityReceived: '',
            q1Expense: '',
            q1RemainingBalance: '0',
            q2BudgetDemand: '',
            q2BudgetExpenditure: '',
            q2AuthorityReceived: '',
            q2Expense: '',
            q2RemainingBalance: '0',
            q3BudgetDemand: '',
            q3BudgetExpenditure: '',
            q3AuthorityReceived: '',
            q3Expense: '',
            q3RemainingBalance: '0',
            q4BudgetDemand: '',
            q4BudgetExpenditure: '',
            q4AuthorityReceived: '',
            q4Expense: '',
            q4RemainingBalance: '0',
            totalBudgetDemand: '',
            totalBudgetExpenditure: '',
            totalAuthorityReceived: '',
            totalExpense: '',
            totalRemainingBalance: '',
            is_uploaded: true
          }));
          
          // Add uploaded items to existing operational rows
          setOperationalRows(prevRows => {
            // Remove any previously uploaded items
            const existingRows = prevRows.filter(row => !row.is_uploaded);
            return [...existingRows, ...newOperationalRows];
          });
        }

        const capitalCount = result.capital_items?.length || 0;
        const operationalCount = result.operational_items?.length || 0;
        
        if (capitalCount > 0 || operationalCount > 0) {
          setMessage(`सफलतापूर्वक अपलोड गरियो: ${capitalCount} पुँजीगत खर्च, ${operationalCount} संचालन खर्च`);
          setMessageType('success');
          
          // Show additional info about what was uploaded
          if (result.errors && result.errors.length > 0) {
            console.log('Upload completed with some errors:', result.errors);
          }
        } else {
          setMessage('फाइल अपलोड भयो तर कुनै वैध डाटा फेला परेन। कृपया फाइल फरमाट जाँच गर्नुहोस्।');
          setMessageType('warning');
        }
      } else {
        setMessage(result.message || 'अपलोड असफल');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage(`अपलोड त्रुटि: ${error.message}`);
      setMessageType('error');
    } finally {
      setUploading(false);
    }
  };

  const addRow = useCallback(() => {
    setRows(prevRows => {
      const newRow = {
        id: prevRows.length > 0 ? Math.max(...prevRows.map(row => row.id)) + 1 : 1,
        codeNumber: '', 
        expenseTitle: '', 
        unallocatedBudget: '', 
        revisedBudget: '',
        q1BudgetDemand: '', 
        q1BudgetExpenditure: '', 
        q1AuthorityReceived: '', 
        q1Expense: '', 
        q1RemainingBalance: '0',
        q2BudgetDemand: '', 
        q2BudgetExpenditure: '', 
        q2AuthorityReceived: '', 
        q2Expense: '', 
        q2RemainingBalance: '0',
        q3BudgetDemand: '', 
        q3BudgetExpenditure: '', 
        q3AuthorityReceived: '', 
        q3Expense: '', 
        q3RemainingBalance: '0',
        q4BudgetDemand: '', 
        q4BudgetExpenditure: '', 
        q4AuthorityReceived: '', 
        q4Expense: '', 
        q4RemainingBalance: '0',
        totalBudgetDemand: '',
        totalBudgetExpenditure: '',
        totalAuthorityReceived: '',
        totalExpense: '',
        totalRemainingBalance: '',
        is_uploaded: false,
        isNewlyAdded: true
      };
      return [...prevRows, newRow];
    });
  }, []);

  const addOperationalRow = useCallback(() => {
    setOperationalRows(prevRows => {
      const newRow = {
        id: `op_${Date.now()}`,
        codeNumber: '', 
        expenseTitle: '', 
        unallocatedBudget: '', 
        revisedBudget: '',
        q1BudgetDemand: '', 
        q1BudgetExpenditure: '', 
        q1AuthorityReceived: '', 
        q1Expense: '', 
        q1RemainingBalance: '0',
        q2BudgetDemand: '', 
        q2BudgetExpenditure: '', 
        q2AuthorityReceived: '', 
        q2Expense: '', 
        q2RemainingBalance: '0',
        q3BudgetDemand: '', 
        q3BudgetExpenditure: '', 
        q3AuthorityReceived: '', 
        q3Expense: '', 
        q3RemainingBalance: '0',
        q4BudgetDemand: '', 
        q4BudgetExpenditure: '', 
        q4AuthorityReceived: '', 
        q4Expense: '', 
        q4RemainingBalance: '0',
        totalBudgetDemand: '',
        totalBudgetExpenditure: '',
        totalAuthorityReceived: '',
        totalExpense: '',
        totalRemainingBalance: '',
        is_uploaded: false,
        isNewlyAdded: true
      };
      return [...prevRows, newRow];
    });
  }, []);

  const handleOperationalRowChange = useCallback((rowId, field, value) => {
    setOperationalRows(prevRows => {
      const updatedRows = prevRows.map(row => 
        row.id === rowId ? { ...row, [field]: value } : row
      );
      
      // Auto-calculate remaining balance immediately
      const quarterMatch = field.match(/^q(\d)(AuthorityReceived|Expense)$/);
      if (quarterMatch) {
        const quarter = quarterMatch[1];
        const authorityField = `q${quarter}AuthorityReceived`;
        const expenseField = `q${quarter}Expense`;
        const remainingField = `q${quarter}RemainingBalance`;
        
        const updatedRow = updatedRows.find(row => row.id === rowId);
        const authority = parseNumericValue(updatedRow[authorityField] || '0');
        const expense = parseNumericValue(updatedRow[expenseField] || '0');
        const remaining = Math.max(0, authority - expense);
        
        // Format the remaining balance according to user's preferred digit format
        const formattedRemaining = formatCalculationResult(remaining, updatedRow);
        
        return updatedRows.map(row => 
          row.id === rowId ? { ...row, [remainingField]: formattedRemaining } : row
        );
      }
      
      // Also recalculate all remaining balances for the row to ensure format consistency
      const updatedRow = updatedRows.find(row => row.id === rowId);
      if (updatedRow) {
        const recalculatedRows = updatedRows.map(row => {
          if (row.id === rowId) {
            // Recalculate all quarter remaining balances
            const quarters = [1, 2, 3, 4];
            const recalculatedRow = { ...row };
            
            quarters.forEach(quarter => {
              const authorityField = `q${quarter}AuthorityReceived`;
              const expenseField = `q${quarter}Expense`;
              const remainingField = `q${quarter}RemainingBalance`;
              
              const authority = parseNumericValue(row[authorityField] || '0');
              const expense = parseNumericValue(row[expenseField] || '0');
              const remaining = Math.max(0, authority - expense);
              
              // Format according to the new detected format
              const formattedRemaining = formatCalculationResult(remaining, recalculatedRow);
              recalculatedRow[remainingField] = formattedRemaining;
            });
            
            return recalculatedRow;
          }
          return row;
        });
        
        return recalculatedRows;
      }
      
      return updatedRows;
    });
  }, []);

  const removeRow = (rowId) => {
    if (rowId.toString().startsWith('op_')) {
      setOperationalRows(prevRows => prevRows.filter(row => row.id !== rowId));
    } else {
      setRows(prevRows => prevRows.filter(row => row.id !== rowId));
    }
  };

  // Office selection handler
  const handleOfficeSelect = (office) => {
    setSelectedOffice(office);
    setShowOfficeDropdown(false);
    setOfficeSearchTerm('');
    // Don't automatically fetch data - let user fill form and submit
  };

  const handleOfficeDropdownToggle = () => {
    setShowOfficeDropdown(!showOfficeDropdown);
  };

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const showPopupMessage = (msg, type) => {
    setPopupMessage(msg);
    setPopupType(type);
    setShowPopup(true);
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      setShowPopup(false);
    }, 3000);
    
    // Clear message after animation completes
    setTimeout(() => {
      setPopupMessage('');
      setPopupType('');
    }, 3300);
  };

  const handleCloseTokenPopup = () => {
    setShowTokenPopup(false);
    // Reset copy success state when closing
    setCopySuccess(false);
  };

  // Form handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form submission - selectedOffice:', selectedOffice);
    console.log('Form submission - selectedFiscalYear:', selectedFiscalYear);
    console.log('Form submission - lastSavedToken:', lastSavedToken);
    console.log('Form submission - viewMode:', viewMode);
    
    if (!selectedOffice?.id || !selectedFiscalYear?.id) {
      showMessage('कृपया कार्यालय र आर्थिक वर्ष छान्नुहोस्', 'error');
      return;
    }
    
    try {
      setLoading(true);
      
      // Prepare data for backend
      const capitalItems = rows.filter(row => 
        row.codeNumber.trim() || row.expenseTitle.trim() || 
        parseNumericValue(row.unallocatedBudget) > 0 || parseNumericValue(row.revisedBudget) > 0
      ).map(row => {
        const item = {
          code_number: row.codeNumber,
          expense_title: row.expenseTitle,
          unallocated_budget: parseNumericValue(row.unallocatedBudget),
          revised_budget: parseNumericValue(row.revisedBudget),
          item_type: 'capital',
          q1_budget_demand: parseNumericValue(row.q1BudgetDemand),
          q1_budget_expenditure: parseNumericValue(row.q1BudgetExpenditure),
          q1_authority_received: parseNumericValue(row.q1AuthorityReceived),
          q1_expense: parseNumericValue(row.q1Expense),
          q2_budget_demand: parseNumericValue(row.q2BudgetDemand),
          q2_budget_expenditure: parseNumericValue(row.q2BudgetExpenditure),
          q2_authority_received: parseNumericValue(row.q2AuthorityReceived),
          q2_expense: parseNumericValue(row.q2Expense),
          q3_budget_demand: parseNumericValue(row.q3BudgetDemand),
          q3_budget_expenditure: parseNumericValue(row.q3BudgetExpenditure),
          q3_authority_received: parseNumericValue(row.q3AuthorityReceived),
          q3_expense: parseNumericValue(row.q3Expense),
          q4_budget_demand: parseNumericValue(row.q4BudgetDemand),
          q4_budget_expenditure: parseNumericValue(row.q4BudgetExpenditure),
          q4_authority_received: parseNumericValue(row.q4AuthorityReceived),
          q4_expense: parseNumericValue(row.q4Expense),
          total_budget_demand: parseNumericValue(row.totalBudgetDemand),
          total_budget_expenditure: parseNumericValue(row.totalBudgetExpenditure),
          total_authority_received: parseNumericValue(row.totalAuthorityReceived),
          total_expense: parseNumericValue(row.totalExpense),
          total_remaining_balance: parseNumericValue(row.totalRemainingBalance)
        };
        
        // Debug logging for q1_budget_demand
        if (row.q1BudgetDemand) {
          console.log('Form submission - Row q1BudgetDemand raw value:', row.q1BudgetDemand);
          console.log('Form submission - Row q1BudgetDemand parsed value:', item.q1_budget_demand);
        }
        
        return item;
      });
      
      const operationalItems = operationalRows.filter(row => 
        row.codeNumber.trim() || row.expenseTitle.trim() || 
        parseNumericValue(row.unallocatedBudget) > 0 || parseNumericValue(row.revisedBudget) > 0
      ).map(row => ({
        code_number: row.codeNumber,
        expense_title: row.expenseTitle,
        unallocated_budget: parseNumericValue(row.unallocatedBudget),
        revised_budget: parseNumericValue(row.revisedBudget),
        item_type: 'operational',
        q1_budget_demand: parseNumericValue(row.q1BudgetDemand),
        q1_budget_expenditure: parseNumericValue(row.q1BudgetExpenditure),
        q1_authority_received: parseNumericValue(row.q1AuthorityReceived),
        q1_expense: parseNumericValue(row.q1Expense),
        q2_budget_demand: parseNumericValue(row.q2BudgetDemand),
        q2_budget_expenditure: parseNumericValue(row.q2BudgetExpenditure),
        q2_authority_received: parseNumericValue(row.q2AuthorityReceived),
        q2_expense: parseNumericValue(row.q2Expense),
        q3_budget_demand: parseNumericValue(row.q3BudgetDemand),
        q3_budget_expenditure: parseNumericValue(row.q3BudgetExpenditure),
        q3_authority_received: parseNumericValue(row.q3AuthorityReceived),
        q3_expense: parseNumericValue(row.q3Expense),
        q4_budget_demand: parseNumericValue(row.q4BudgetDemand),
        q4_budget_expenditure: parseNumericValue(row.q4BudgetExpenditure),
        q4_authority_received: parseNumericValue(row.q4AuthorityReceived),
        q4_expense: parseNumericValue(row.q4Expense),
        total_budget_demand: parseNumericValue(row.totalBudgetDemand),
        total_budget_expenditure: parseNumericValue(row.totalBudgetExpenditure),
        total_authority_received: parseNumericValue(row.totalAuthorityReceived),
        total_expense: parseNumericValue(row.totalExpense),
        total_remaining_balance: parseNumericValue(row.totalRemainingBalance)
      }));
      
      const allItems = [...capitalItems, ...operationalItems];
      
      if (allItems.length === 0) {
        showMessage('कृपया कम्तिमा एउटा खर्च आइटम भर्नुहोस्', 'error');
        return;
      }
      
      // Validate that each item has required fields
      for (let i = 0; i < allItems.length; i++) {
        const item = allItems[i];
        if (!item.code_number || !item.expense_title) {
          showMessage(`कृपया आइटम ${i + 1} मा कोड नम्बर र खर्च शीर्षक भर्नुहोस्`, 'error');
          return;
        }
      }

      let response;
      let result;

      // Check if we're updating existing data or creating new data
      if (lastSavedToken && lastSavedToken !== 'new') {
        console.log('Updating existing data with token:', lastSavedToken);
        // Update existing data - include institution, fiscal_year, items and remarks
        const updateData = {
          institution: selectedOffice.id, // Send ID since backend handles it
          fiscal_year: selectedFiscalYear.id, // Send ID since backend handles it
          expenditure_items: allItems,
          remarks: '',
          ...incomePayloadFromState(incomeTotals)
        };
        console.log('Sending update data:', updateData);
        console.log('Update URL:', `${CAPITAL_EXPENDITURE_API}/${lastSavedToken}/update/`);
        
        response = await fetch(`${CAPITAL_EXPENDITURE_API}/${lastSavedToken}/update/`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(updateData)
        });
        
        result = await response.json();
        console.log('Update response:', result);
        
        if (result.status === 'success') {
          showPopupMessage('डाटा सफलतापूर्वक अपडेट भयो!', 'success');
        } else {
          showMessage(`डाटा अपडेट गर्न समस्या भयो: ${result.message}`, 'error');
        }
      } else {
        console.log('Creating new data - lastSavedToken is:', lastSavedToken);
        
        const requestData = {
          institution_id: selectedOffice.id,
          fiscal_year_id: selectedFiscalYear.id,
          items: allItems,
          remarks: '',
          ...incomePayloadFromState(incomeTotals)
        };
        
        console.log('Sending data to backend:', requestData);
        console.log('API URL:', BULK_CREATE_IMPROVED_API);
        
        // Create new data
        response = await fetch(BULK_CREATE_IMPROVED_API, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(requestData)
        });
        
        result = await response.json();
        console.log('Backend response:', result);
        
        if (result.status === 'success') {
          setLastSavedToken(result.data.token);
          
          // Show special token popup
          setTokenData({
            token: result.data.token,
            institution: selectedOffice.name,
            fiscalYear: selectedFiscalYear.name
          });
          setShowTokenPopup(true);
          
          // Clear all input fields but keep the form structure
          setRows(rows.map(row => ({
            ...row,
            codeNumber: '',
            expenseTitle: '',
            unallocatedBudget: '',
            revisedBudget: '',
            q1BudgetDemand: '',
            q1BudgetExpenditure: '',
            q1AuthorityReceived: '',
            q1Expense: '',
            q1RemainingBalance: '0',
            q2BudgetDemand: '',
            q2BudgetExpenditure: '',
            q2AuthorityReceived: '',
            q2Expense: '',
            q2RemainingBalance: '0',
            q3BudgetDemand: '',
            q3BudgetExpenditure: '',
            q3AuthorityReceived: '',
            q3Expense: '',
            q3RemainingBalance: '0',
            q4BudgetDemand: '',
            q4BudgetExpenditure: '',
            q4AuthorityReceived: '',
            q4Expense: '',
            q4RemainingBalance: '0',
            totalBudgetDemand: '',
            totalBudgetExpenditure: '',
            totalAuthorityReceived: '',
            totalExpense: '',
            totalRemainingBalance: '',
          })));
          
          setOperationalRows(operationalRows.map(row => ({
            ...row,
            codeNumber: '',
            expenseTitle: '',
            unallocatedBudget: '',
            revisedBudget: '',
            q1BudgetDemand: '',
            q1BudgetExpenditure: '',
            q1AuthorityReceived: '',
            q1Expense: '',
            q1RemainingBalance: '0',
            q2BudgetDemand: '',
            q2BudgetExpenditure: '',
            q2AuthorityReceived: '',
            q2Expense: '',
            q2RemainingBalance: '0',
            q3BudgetDemand: '',
            q3BudgetExpenditure: '',
            q3AuthorityReceived: '',
            q3Expense: '',
            q3RemainingBalance: '0',
            q4BudgetDemand: '',
            q4BudgetExpenditure: '',
            q4AuthorityReceived: '',
            q4Expense: '',
            q4RemainingBalance: '0',
            totalBudgetDemand: '',
            totalBudgetExpenditure: '',
            totalAuthorityReceived: '',
            totalExpense: '',
            totalRemainingBalance: '',
          })));

          setIncomeTotals(emptyIncomeTotals());
          
          // Keep office and fiscal year selection for convenience
          setLastSavedToken('new');
        } else {
          showMessage(`डाटा सेभ गर्न समस्या भयो: ${result.message}`, 'error');
        }
      }
      
    } catch (error) {
      console.error('Error saving data:', error);
      showMessage('डाटा सेभ गर्न समस्या भयो', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setRows([{ 
      id: 1, 
      codeNumber: '', 
      expenseTitle: '', 
      unallocatedBudget: '', 
      revisedBudget: '', 
      q1BudgetDemand: '', 
      q1BudgetExpenditure: '', 
      q1AuthorityReceived: '', 
      q1Expense: '', 
      q1RemainingBalance: '0',
      q2BudgetDemand: '', 
      q2BudgetExpenditure: '', 
      q2AuthorityReceived: '', 
      q2Expense: '', 
      q2RemainingBalance: '0',
      q3BudgetDemand: '', 
      q3BudgetExpenditure: '', 
      q3AuthorityReceived: '', 
      q3Expense: '', 
      q3RemainingBalance: '0',
      q4BudgetDemand: '', 
      q4BudgetExpenditure: '', 
      q4AuthorityReceived: '', 
      q4Expense: '', 
      q4RemainingBalance: '0',
      totalBudgetDemand: '',
      totalBudgetExpenditure: '',
      totalAuthorityReceived: '',
      totalExpense: '',
      totalRemainingBalance: '',
      is_uploaded: false
    }]);
    setOperationalRows([{ 
      id: 'op_1', 
      codeNumber: '', 
      expenseTitle: '', 
      unallocatedBudget: '', 
      revisedBudget: '', 
      q1BudgetDemand: '', 
      q1BudgetExpenditure: '', 
      q1AuthorityReceived: '', 
      q1Expense: '', 
      q1RemainingBalance: '0',
      q2BudgetDemand: '', 
      q2BudgetExpenditure: '', 
      q2AuthorityReceived: '', 
      q2Expense: '', 
      q2RemainingBalance: '0',
      q3BudgetDemand: '', 
      q3BudgetExpenditure: '', 
      q3AuthorityReceived: '', 
      q3Expense: '', 
      q3RemainingBalance: '0',
      q4BudgetDemand: '', 
      q4BudgetExpenditure: '', 
      q4AuthorityReceived: '', 
      q4Expense: '', 
      q4RemainingBalance: '0',
      totalBudgetDemand: '',
      totalBudgetExpenditure: '',
      totalAuthorityReceived: '',
      totalExpense: '',
      totalRemainingBalance: '',
      is_uploaded: false
    }]);
    setSelectedOffice({ id: null, name: '' });
    setSelectedFiscalYear(pickCurrentFiscalYear(fiscalYears));
    setLastSavedToken('new');
    setViewMode(false); // Reset to edit mode
    setIncomeTotals(emptyIncomeTotals());
  };
  
  // Function to clear form for new data entry
  const handleClearForNewData = () => {
    setRows([{ 
      id: 1, 
      codeNumber: '', 
      expenseTitle: '', 
      unallocatedBudget: '', 
      revisedBudget: '', 
      q1BudgetDemand: '', 
      q1BudgetExpenditure: '', 
      q1AuthorityReceived: '', 
      q1Expense: '', 
      q1RemainingBalance: '',
      q2BudgetDemand: '', 
      q2BudgetExpenditure: '', 
      q2AuthorityReceived: '', 
      q2Expense: '', 
      q2RemainingBalance: '',
      q3BudgetDemand: '', 
      q3BudgetExpenditure: '', 
      q3AuthorityReceived: '', 
      q3Expense: '', 
      q3RemainingBalance: '',
      q4BudgetDemand: '', 
      q4BudgetExpenditure: '', 
      q4AuthorityReceived: '', 
      q4Expense: '', 
      q4RemainingBalance: '',
      totalBudgetDemand: '',
      totalBudgetExpenditure: '',
      totalAuthorityReceived: '',
      totalExpense: '',
      totalRemainingBalance: '',
      is_uploaded: false
    }]);
    
    setOperationalRows([{ 
      id: 'op_1', 
      codeNumber: '', 
      expenseTitle: '', 
      unallocatedBudget: '', 
      revisedBudget: '', 
      q1BudgetDemand: '', 
      q1BudgetExpenditure: '', 
      q1AuthorityReceived: '', 
      q1Expense: '', 
      q1RemainingBalance: '0',
      q2BudgetDemand: '', 
      q2BudgetExpenditure: '', 
      q2AuthorityReceived: '', 
      q2Expense: '', 
      q2RemainingBalance: '0',
      q3BudgetDemand: '', 
      q3BudgetExpenditure: '', 
      q3AuthorityReceived: '', 
      q3Expense: '', 
      q3RemainingBalance: '0',
      q4BudgetDemand: '', 
      q4BudgetExpenditure: '', 
      q4AuthorityReceived: '', 
      q4Expense: '', 
      q4RemainingBalance: '0',
      totalBudgetDemand: '',
      totalBudgetExpenditure: '',
      totalAuthorityReceived: '',
      totalExpense: '',
      totalRemainingBalance: '',
      is_uploaded: false
    }]);
    
    // Reset office and fiscal year selection
    setSelectedOffice({ id: null, name: '' });
    setSelectedFiscalYear(pickCurrentFiscalYear(fiscalYears));
    
    // Reset the token for new data entry
    setLastSavedToken('new');
    
    // Reset view mode
    setViewMode(false);

    setIncomeTotals(emptyIncomeTotals());
    
    showPopupMessage('फारम नयाँ डाटा थप्नको लागि सफा गरियो', 'success');
    console.log('Form cleared for new data entry');
  };

  // Data view handlers (actual implementation)

  const handleEdit = async (expenditureData) => {
    try {
      console.log('Editing expenditure data:', expenditureData);
      
      // Fetch detailed data for the selected expenditure
      const response = await fetch(`${CAPITAL_EXPENDITURE_API}/${expenditureData.id}/`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        console.log('Detailed data fetched:', data.data);
        console.log('Financial data in fetched data:', data.data.financial_data);
        setEditFormData(data.data);
        setShowEditForm(true);
        showPopupMessage('सम्पादन फारम खुल्यो', 'success');
      } else {
        showMessage('डाटा लोड गर्न समस्या भयो', 'error');
      }
    } catch (error) {
      console.error('Error loading data for editing:', error);
      showMessage('डाटा लोड गर्न समस्या भयो', 'error');
    }
  };

  // Function to clear main form state
  const clearMainFormState = () => {
    setRows([{ 
      id: 1, 
      codeNumber: '', 
      expenseTitle: '', 
      unallocatedBudget: '', 
      revisedBudget: '', 
      q1BudgetDemand: '', 
      q1BudgetExpenditure: '', 
      q1AuthorityReceived: '', 
      q1Expense: '', 
      q1RemainingBalance: '',
      q2BudgetDemand: '', 
      q2BudgetExpenditure: '', 
      q2AuthorityReceived: '', 
      q2Expense: '', 
      q2RemainingBalance: '',
      q3BudgetDemand: '', 
      q3BudgetExpenditure: '', 
      q3AuthorityReceived: '', 
      q3Expense: '', 
      q3RemainingBalance: '',
      q4BudgetDemand: '', 
      q4BudgetExpenditure: '', 
      q4AuthorityReceived: '', 
      q4Expense: '', 
      q4RemainingBalance: '',
      totalBudgetDemand: '',
      totalBudgetExpenditure: '',
      totalAuthorityReceived: '',
      totalExpense: '',
      totalRemainingBalance: '',
      is_uploaded: false
    }]);
    
    setOperationalRows([{ 
      id: 'op_1', 
      codeNumber: '', 
      expenseTitle: '', 
      unallocatedBudget: '', 
      revisedBudget: '', 
      q1BudgetDemand: '', 
      q1BudgetExpenditure: '', 
      q1AuthorityReceived: '', 
      q1Expense: '', 
      q1RemainingBalance: '',
      q2BudgetDemand: '', 
      q2BudgetExpenditure: '', 
      q2AuthorityReceived: '', 
      q2Expense: '', 
      q2RemainingBalance: '',
      q3BudgetDemand: '', 
      q3BudgetExpenditure: '', 
      q3AuthorityReceived: '', 
      q3Expense: '', 
      q3RemainingBalance: '',
      q4BudgetDemand: '', 
      q4BudgetExpenditure: '', 
      q4AuthorityReceived: '', 
      q4Expense: '', 
      q4RemainingBalance: '',
      totalBudgetDemand: '',
      totalBudgetExpenditure: '',
      totalAuthorityReceived: '',
      totalExpense: '',
      totalRemainingBalance: '',
      is_uploaded: false
    }]);

    setIncomeTotals(emptyIncomeTotals());
  };

  // Function to load expenditure data into the form
  const loadExpenditureDataIntoForm = async (expenditureData) => {
    try {
      console.log('Loading expenditure data:', expenditureData);
      console.log('Expenditure items:', expenditureData.expenditure_items);
      console.log('Full expenditure data structure:', JSON.stringify(expenditureData, null, 2));
      
      // Set the office and fiscal year - handle different data structures
      console.log('Raw institution data:', expenditureData.institution);
      console.log('Raw fiscal_year data:', expenditureData.fiscal_year);
      console.log('Raw institution_id:', expenditureData.institution_id);
      console.log('Raw fiscal_year_id:', expenditureData.fiscal_year_id);
      console.log('Raw institution_name:', expenditureData.institution_name);
      console.log('Raw fiscal_year_name:', expenditureData.fiscal_year_name);
      
      // Extract office data - backend sends institution as ID and institution_name as string
      if (expenditureData.institution && expenditureData.institution_name) {
        const officeData = {
          id: expenditureData.institution, // This is the ID
          name: expenditureData.institution_name // This is the name
        };
        console.log('Setting selected office from institution + institution_name:', officeData);
        setSelectedOffice(officeData);
      } else if (expenditureData.institution_id && expenditureData.institution_name) {
        const officeData = {
          id: expenditureData.institution_id,
          name: expenditureData.institution_name
        };
        console.log('Setting selected office from institution_id + institution_name:', officeData);
        setSelectedOffice(officeData);
      }
      
      // Extract fiscal year data - backend sends fiscal_year as ID and fiscal_year_display as string
      if (expenditureData.fiscal_year && expenditureData.fiscal_year_display) {
        const fiscalYearData = {
          id: expenditureData.fiscal_year, // This is the ID
          name: expenditureData.fiscal_year_display // This is the display name
        };
        console.log('Setting selected fiscal year from fiscal_year + fiscal_year_display:', fiscalYearData);
        setSelectedFiscalYear(fiscalYearData);
      } else if (expenditureData.fiscal_year_id && expenditureData.fiscal_year_name) {
        const fiscalYearData = {
          id: expenditureData.fiscal_year_id,
          name: expenditureData.fiscal_year_name
        };
        console.log('Setting selected fiscal year from fiscal_year_id + fiscal_year_name:', fiscalYearData);
        setSelectedFiscalYear(fiscalYearData);
      }

      // Load expenditure items into the form
      if (expenditureData.expenditure_items && expenditureData.expenditure_items.length > 0) {
        console.log('Processing expenditure items:', expenditureData.expenditure_items.length);
        const capitalItems = [];
        const operationalItems = [];

        expenditureData.expenditure_items.forEach((item, index) => {
          console.log(`Processing item ${index}:`, item);
          const formItem = {
            id: item.id,
            codeNumber: item.code_number || '',
            expenseTitle: item.expense_title || '',
            unallocatedBudget: item.unallocated_budget || '',
            revisedBudget: item.revised_budget || '',
            q1BudgetDemand: item.q1_budget_demand || '',
            q1BudgetExpenditure: item.q1_budget_expenditure || '',
            q1AuthorityReceived: item.q1_authority_received || '',
            q1Expense: item.q1_expense || '',
            q1RemainingBalance: item.q1_remaining_balance || '',
            q2BudgetDemand: item.q2_budget_demand || '',
            q2BudgetExpenditure: item.q2_budget_expenditure || '',
            q2AuthorityReceived: item.q2_authority_received || '',
            q2Expense: item.q2_expense || '',
            q2RemainingBalance: item.q2_remaining_balance || '',
            q3BudgetDemand: item.q3_budget_demand || '',
            q3BudgetExpenditure: item.q3_budget_expenditure || '',
            q3AuthorityReceived: item.q3_authority_received || '',
            q3Expense: item.q3_expense || '',
            q3RemainingBalance: item.q3_remaining_balance || '',
            q4BudgetDemand: item.q4_budget_demand || '',
            q4BudgetExpenditure: item.q4_budget_expenditure || '',
            q4AuthorityReceived: item.q4_authority_received || '',
            q4Expense: item.q4_expense || '',
            q4RemainingBalance: item.q4_remaining_balance || '',
            totalBudgetDemand: blankIfZero(item.total_budget_demand),
            totalBudgetExpenditure: blankIfZero(item.total_budget_expenditure),
            totalAuthorityReceived: blankIfZero(item.total_authority_received),
            totalExpense: blankIfZero(item.total_expense),
            totalRemainingBalance: blankIfZero(item.total_remaining_balance),
          };

          if (item.item_type === 'capital') {
            capitalItems.push(formItem);
          } else if (item.item_type === 'operational') {
            operationalItems.push(formItem);
          }
        });

        console.log('Capital items prepared:', capitalItems);
        console.log('Operational items prepared:', operationalItems);

        // Update the form rows
        if (capitalItems.length > 0) {
          console.log('Setting capital rows:', capitalItems);
          setRows(capitalItems);
        } else {
          // If no capital items, set at least one empty row
          console.log('No capital items, setting empty row');
          setRows([{ 
            id: 1, 
            codeNumber: '', 
            expenseTitle: '', 
            unallocatedBudget: '', 
            revisedBudget: '', 
            q1BudgetDemand: '', 
            q1BudgetExpenditure: '', 
            q1AuthorityReceived: '', 
            q1Expense: '', 
            q1RemainingBalance: '',
            q2BudgetDemand: '', 
            q2BudgetExpenditure: '', 
            q2AuthorityReceived: '', 
            q2Expense: '', 
            q2RemainingBalance: '',
            q3BudgetDemand: '', 
            q3BudgetExpenditure: '', 
            q3AuthorityReceived: '', 
            q3Expense: '', 
            q3RemainingBalance: '',
            q4BudgetDemand: '', 
            q4BudgetExpenditure: '', 
            q4AuthorityReceived: '', 
            q4Expense: '', 
            q4RemainingBalance: '',
            totalBudgetDemand: '',
            totalBudgetExpenditure: '',
            totalAuthorityReceived: '',
            totalExpense: '',
            totalRemainingBalance: '',
            is_uploaded: false
          }]);
        }
        
        if (operationalItems.length > 0) {
          console.log('Setting operational rows:', operationalItems);
          setOperationalRows(operationalItems);
        } else {
          // If no operational items, set at least one empty row
          console.log('No operational items, setting empty row');
          setOperationalRows([{ 
            id: 'op_1', 
            codeNumber: '', 
            expenseTitle: '', 
            unallocatedBudget: '', 
            revisedBudget: '', 
            q1BudgetDemand: '', 
            q1BudgetExpenditure: '', 
            q1AuthorityReceived: '', 
            q1Expense: '', 
            q1RemainingBalance: '0',
            q2BudgetDemand: '', 
            q2BudgetExpenditure: '', 
            q2AuthorityReceived: '', 
            q2Expense: '', 
            q2RemainingBalance: '0',
            q3BudgetDemand: '', 
            q3BudgetExpenditure: '', 
            q3AuthorityReceived: '', 
            q3Expense: '', 
            q3RemainingBalance: '0',
            q4BudgetDemand: '', 
            q4BudgetExpenditure: '', 
            q4AuthorityReceived: '', 
            q4Expense: '', 
            q4RemainingBalance: '0',
            totalBudgetDemand: '',
            totalBudgetExpenditure: '',
            totalAuthorityReceived: '',
            totalExpense: '',
            totalRemainingBalance: '',
            is_uploaded: false
          }]);
        }
      } else {
        console.log('No expenditure items found in data');
      }

      setIncomeTotals(incomeStateFromApi(expenditureData));

      // Store the current expenditure ID for updating
      setLastSavedToken(expenditureData.id); // Always use the ID for updates
      
      // Update existingData to maintain consistency with the form
      // This ensures that when we switch back to data view, the data is properly formatted
      const formattedData = {
        id: expenditureData.id || null,
        institution_name: expenditureData.institution?.name || expenditureData.institution_name || '',
        institution_code: expenditureData.institution?.code || expenditureData.institution_code || '',
        fiscal_year_display: expenditureData.fiscal_year?.name || expenditureData.fiscal_year_name || '',
        token: expenditureData.token || '',
        status: expenditureData.status || 'draft',
        created_at: expenditureData.created_at || new Date().toISOString(),
        updated_at: expenditureData.updated_at || new Date().toISOString(),
        total_budget: expenditureData.total_budget || 0,
        revised_budget: expenditureData.revised_budget || 0
      };
      
      // Update existingData to include this formatted record
      if (formattedData && formattedData.id) {
        setExistingData(prevData => {
          // Ensure prevData is always an array
          const safePrevData = Array.isArray(prevData) ? prevData : [];
          
          // Check if this record already exists
          const existingIndex = safePrevData.findIndex(item => item.id === formattedData.id);
          if (existingIndex >= 0) {
            // Update existing record
            const updatedData = [...safePrevData];
            updatedData[existingIndex] = formattedData;
            return updatedData;
          } else {
            // Add new record
            return [...safePrevData, formattedData];
          }
        });
      }
      
      console.log('Data successfully loaded into form');
      showMessage('डाटा फारममा सफलतापूर्वक लोड भयो', 'success');
      
    } catch (error) {
      console.error('Error loading data into form:', error);
      showMessage('फारममा डाटा लोड गर्न समस्या भयो', 'error');
    }
  };

  const handleDelete = async (expenditure) => {
    if (!window.confirm(`के तपाईं यो डाटा मेटाउन चाहनुहुन्छ?\nसंस्था: ${expenditure.institution_name}\nआर्थिक वर्ष: ${expenditure.fiscal_year_display}`)) {
      return;
    }
    
    try {
      setLoading(true);
              const response = await fetch(`${CAPITAL_EXPENDITURE_API}/${expenditure.id}/delete/`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        showMessage('डाटा सफलतापूर्वक मेटाइयो', 'success');
        showPopupMessage('डाटा सफलतापूर्वक मेटाइयो', 'success');
        // Refresh data from backend
        await fetchAllSavedData();
      } else {
        showMessage(`डाटा मेटाउन समस्या भयो: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Error deleting expenditure:', error);
      showMessage('डाटा मेटाउन समस्या भयो', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedDataForEdit) return;
    
    try {
      setLoading(true);
      
      // Debug logging to see what data is being sent
      console.log('handleUpdate - selectedDataForEdit:', selectedDataForEdit);
      
      const response = await fetch(`${CAPITAL_EXPENDITURE_API}/${selectedDataForEdit.id}/update/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(selectedDataForEdit)
      });
      
      const result = await response.json();
      console.log('handleUpdate - Backend response:', result);
      
      if (result.status === 'success') {
        showMessage('डाटा सफलतापूर्वक अपडेट भयो', 'success');
        setSelectedDataForEdit(null);
        // Refresh data from backend
        await fetchAllSavedData();
      } else {
        showMessage(`डाटा अपडेट गर्न समस्या भयो: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Error updating expenditure:', error);
      showMessage('डाटा अपडेट गर्न समस्या भयो', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Edit form handlers
  const handleEditFormSave = async (formData) => {
    try {
      setLoading(true);
      console.log('handleEditFormSave - formData:', formData);
      console.log('handleEditFormSave - editFormData:', editFormData);

      const capitalItems = formData.rows.map(row => ({
        code_number: row.codeNumber,
        expense_title: row.expenseTitle,
        unallocated_budget: parseNumericValue(row.unallocatedBudget),
        revised_budget: parseNumericValue(row.revisedBudget),
        item_type: 'capital',
        q1_budget_demand: parseNumericValue(row.q1BudgetDemand),
        q1_budget_expenditure: parseNumericValue(row.q1BudgetExpenditure),
        q1_authority_received: parseNumericValue(row.q1AuthorityReceived),
        q1_expense: parseNumericValue(row.q1Expense),
        q1_remaining_balance: parseNumericValue(row.q1RemainingBalance),
        q2_budget_demand: parseNumericValue(row.q2BudgetDemand),
        q2_budget_expenditure: parseNumericValue(row.q2BudgetExpenditure),
        q2_authority_received: parseNumericValue(row.q2AuthorityReceived),
        q2_expense: parseNumericValue(row.q2Expense),
        q2_remaining_balance: parseNumericValue(row.q2RemainingBalance),
        q3_budget_demand: parseNumericValue(row.q3BudgetDemand),
        q3_budget_expenditure: parseNumericValue(row.q3BudgetExpenditure),
        q3_authority_received: parseNumericValue(row.q3AuthorityReceived),
        q3_expense: parseNumericValue(row.q3Expense),
        q3_remaining_balance: parseNumericValue(row.q3RemainingBalance),
        q4_budget_demand: parseNumericValue(row.q4BudgetDemand),
        q4_budget_expenditure: parseNumericValue(row.q4BudgetExpenditure),
        q4_authority_received: parseNumericValue(row.q4AuthorityReceived),
        q4_expense: parseNumericValue(row.q4Expense),
        q4_remaining_balance: parseNumericValue(row.q4RemainingBalance),
        total_budget_demand: parseNumericValue(row.totalBudgetDemand),
        total_budget_expenditure: parseNumericValue(row.totalBudgetExpenditure),
        total_authority_received: parseNumericValue(row.totalAuthorityReceived),
        total_expense: parseNumericValue(row.totalExpense),
        total_remaining_balance: parseNumericValue(row.totalRemainingBalance)
      }));

      const operationalItems = formData.operationalRows.map(row => ({
        code_number: row.codeNumber,
        expense_title: row.expenseTitle,
        unallocated_budget: parseNumericValue(row.unallocatedBudget),
        revised_budget: parseNumericValue(row.revisedBudget),
        item_type: 'operational',
        q1_budget_demand: parseNumericValue(row.q1BudgetDemand),
        q1_budget_expenditure: parseNumericValue(row.q1BudgetExpenditure),
        q1_authority_received: parseNumericValue(row.q1AuthorityReceived),
        q1_expense: parseNumericValue(row.q1Expense),
        q1_remaining_balance: parseNumericValue(row.q1RemainingBalance),
        q2_budget_demand: parseNumericValue(row.q2BudgetDemand),
        q2_budget_expenditure: parseNumericValue(row.q2BudgetExpenditure),
        q2_authority_received: parseNumericValue(row.q2AuthorityReceived),
        q2_expense: parseNumericValue(row.q2Expense),
        q2_remaining_balance: parseNumericValue(row.q2RemainingBalance),
        q3_budget_demand: parseNumericValue(row.q3BudgetDemand),
        q3_budget_expenditure: parseNumericValue(row.q3BudgetExpenditure),
        q3_authority_received: parseNumericValue(row.q3AuthorityReceived),
        q3_expense: parseNumericValue(row.q3Expense),
        q3_remaining_balance: parseNumericValue(row.q3RemainingBalance),
        q4_budget_demand: parseNumericValue(row.q4BudgetDemand),
        q4_budget_expenditure: parseNumericValue(row.q4BudgetExpenditure),
        q4_authority_received: parseNumericValue(row.q4AuthorityReceived),
        q4_expense: parseNumericValue(row.q4Expense),
        q4_remaining_balance: parseNumericValue(row.q4RemainingBalance),
        total_budget_demand: parseNumericValue(row.totalBudgetDemand),
        total_budget_expenditure: parseNumericValue(row.totalBudgetExpenditure),
        total_authority_received: parseNumericValue(row.totalAuthorityReceived),
        total_expense: parseNumericValue(row.totalExpense),
        total_remaining_balance: parseNumericValue(row.totalRemainingBalance)
      }));

      const allItems = [...capitalItems, ...operationalItems];
      const updateData = {
        items: allItems,
        remarks: '',
        ...incomePayloadFromState(formData.incomeTotals || emptyIncomeTotals())
      };

      const response = await fetch(`${CAPITAL_EXPENDITURE_API}/${formData.id}/bulk-update/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData)
      });

      const result = await response.json();
      if (result.status === 'success') {
        showPopupMessage('डाटा सफलतापूर्वक अपडेट भयो!', 'success');
        await fetchAllSavedData();
      } else {
        showMessage(`डाटा अपडेट गर्न समस्या भयो: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Error updating data:', error);
      showMessage('डाटा अपडेट गर्न समस्या भयो', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditFormCancel = () => {
    setShowEditForm(false);
    setEditFormData(null);
    setTokenYearSwitchLoading(false);
    if (cameFromTokenSearch) {
      navigate('/token-search');
      setCameFromTokenSearch(false);
      return;
    }
    clearMainFormState();
    setViewMode(false);
    showPopupMessage('सम्पादन रद्द गरियो', 'info');
  };

  const handleTokenFormYearChange = async (fiscalYearId) => {
    const sourceToken = editFormData?.source_token;
    const nextYearId = Number(fiscalYearId);
    if (!sourceToken || !nextYearId || Number(editFormData?.fiscal_year) === nextYearId) {
      return;
    }

    setTokenYearSwitchLoading(true);
    try {
      const response = await fetch(TOKEN_SEARCH_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: sourceToken,
          fiscal_year_id: nextYearId,
        }),
      });
      const result = await response.json();
      if (result.status === 'success' && result.data?.capital_expenditure) {
        setEditFormData(mapTokenSearchToEditForm(result.data, sourceToken));
      } else {
        showMessage(result.message || 'आर्थिक वर्ष बदल्न सकिएन', 'error');
      }
    } catch (error) {
      console.error('Error switching token form year:', error);
      showMessage('आर्थिक वर्ष बदल्न सकिएन', 'error');
    } finally {
      setTokenYearSwitchLoading(false);
    }
  };

  // Export and print functionality
  const handleExportExcel = async (expenditureId) => {
    try {
      setLoading(true);
      
      // First, get the basic expenditure info from the existing data
      const expenditureInfo = existingData.find(item => item.id === expenditureId);
      
      if (!expenditureInfo) {
        showMessage('डाटा फेला परेन', 'error');
        return;
      }
      
      // Fetch the detailed expenditure data
      const response = await fetch(`${CAPITAL_EXPENDITURE_API}/${expenditureId}/`, {
        headers: getAuthHeaders()
      });
      const result = await response.json();
        
        if (result.status === 'success') {
          // Create a single record array for organized export
          const singleRecord = [{
            ...expenditureInfo,
            expenditure_items: result.data.expenditure_items || []
          }];
          
          // Use the organized Excel export utility for single record
          const exportResult = exportOrganizedToExcel(singleRecord, `expenditure_${expenditureId}`);
          
          if (exportResult.success) {
            showMessage(`Excel फाइल सफलतापूर्वक डाउनलोड भयो: ${exportResult.filename}`, 'success');
          } else {
            showMessage(`Excel डाउनलोड गर्न समस्या भयो: ${exportResult.error}`, 'error');
          }
        } else {
          showMessage('डाटा लोड गर्न समस्या भयो', 'error');
        }
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showMessage('Excel डाउनलोड गर्न समस्या भयो', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Export filtered data as Excel
  const handleExportFilteredData = async () => {
    try {
      setLoading(true);
      
      // Get the current display data (filtered or all)
      const dataToExport = filteredData.length > 0 ? filteredData : existingData;
      
      if (!dataToExport || dataToExport.length === 0) {
        showMessage('डाउनलोड गर्न कुनै डाटा छैन', 'error');
        return;
      }

      // Fetch detailed data for each expenditure record
      const detailedRecords = [];
      
      for (const expenditure of dataToExport) {
        try {
          // Fetch detailed data for each expenditure
          const response = await fetch(`${CAPITAL_EXPENDITURE_API}/${expenditure.id}/`, {
            headers: getAuthHeaders()
          });
          
          if (response.ok) {
            const result = await response.json();
            
            if (result.status === 'success') {
              // Add the detailed record with expenditure items
              detailedRecords.push({
                ...expenditure,
                expenditure_items: result.data.expenditure_items || []
              });
            }
          }
        } catch (error) {
          console.error(`Error fetching details for expenditure ${expenditure.id}:`, error);
        }
      }

      if (detailedRecords.length === 0) {
        showMessage('डाउनलोड गर्न कुनै डाटा छैन', 'error');
        return;
      }

      // Generate filename based on filter status
      const isFiltered = filteredData.length > 0;
      const filename = isFiltered ? 'filtered_expenditure_data' : 'all_expenditure_data';
      
      // Export to Excel using organized format
      const exportResult = exportOrganizedToExcel(detailedRecords, filename);
      
      if (exportResult.success) {
        const message = isFiltered 
          ? `फिल्टर गरिएको डाटा सफलतापूर्वक डाउनलोड भयो: ${exportResult.filename}`
          : `सबै डाटा सफलतापूर्वक डाउनलोड भयो: ${exportResult.filename}`;
        showMessage(message, 'success');
      } else {
        showMessage(`Excel डाउनलोड गर्न समस्या भयो: ${exportResult.error}`, 'error');
      }
    } catch (error) {
      console.error('Error exporting filtered Excel:', error);
      showMessage('Excel डाउनलोड गर्न समस्या भयो', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (expenditureId) => {
    try {
      const response = await fetch(`${CAPITAL_EXPENDITURE_API}/${expenditureId}/print/`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const printWindow = window.open('', '_blank');
        const htmlContent = await response.text();
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.print();
        showMessage('प्रिन्ट कार्य सुरु भयो', 'success');
      } else {
        showMessage('प्रिन्ट गर्न समस्या भयो', 'error');
      }
    } catch (error) {
      console.error('Error printing:', error);
      showMessage('प्रिन्ट गर्न समस्या भयो', 'error');
    }
  };

  // Download current form data as Excel
  const handleDownloadFormData = () => {
    try {
      // Check if we have the required data
      if (!selectedOffice?.id || !selectedFiscalYear?.id) {
        showMessage('कृपया संस्था र आर्थिक वर्ष छान्नुहोस्', 'error');
        return;
      }

      // Check if we have any data to export
      const hasData = rows.some(row => 
        row.codeNumber.trim() || 
        row.expenseTitle.trim() || 
        parseNumericValue(row.unallocatedBudget) > 0 || 
        parseNumericValue(row.revisedBudget) > 0
      ) || operationalRows.some(row => 
        row.codeNumber.trim() || 
        row.expenseTitle.trim() || 
        parseNumericValue(row.unallocatedBudget) > 0 || 
        parseNumericValue(row.revisedBudget) > 0
      );

      if (!hasData) {
        showMessage('डाउनलोड गर्न कुनै डाटा छैन', 'error');
        return;
      }

      // Prepare form data for export
      const formData = {
        rows: rows.filter(row => 
          row.codeNumber.trim() || 
          row.expenseTitle.trim() || 
          parseNumericValue(row.unallocatedBudget) > 0 || 
          parseNumericValue(row.revisedBudget) > 0
        ),
        operationalRows: operationalRows.filter(row => 
          row.codeNumber.trim() || 
          row.expenseTitle.trim() || 
          parseNumericValue(row.unallocatedBudget) > 0 || 
          parseNumericValue(row.revisedBudget) > 0
        )
      };

      // Export to Excel
      const exportResult = exportFormDataToExcel(formData, selectedOffice, selectedFiscalYear);
      
      if (exportResult.success) {
        showMessage(`Excel फाइल सफलतापूर्वक डाउनलोड भयो: ${exportResult.filename}`, 'success');
      } else {
        showMessage(`Excel डाउनलोड गर्न समस्या भयो: ${exportResult.error}`, 'error');
      }
    } catch (error) {
      console.error('Error downloading form data:', error);
      showMessage('Excel डाउनलोड गर्न समस्या भयो', 'error');
    }
  };

  // Individual item operations
  const handleItemUpdate = async (itemId, updatedData) => {
    try {
      setLoading(true);
      const response = await fetch(`${EXPENDITURE_ITEMS_API}/${itemId}/update/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          code_number: updatedData.codeNumber,
          expense_title: updatedData.expenseTitle,
          unallocated_budget: parseNumericValue(updatedData.unallocatedBudget),
          revised_budget: parseNumericValue(updatedData.revisedBudget),
          q1_budget_demand: parseNumericValue(updatedData.q1BudgetDemand),
          q1_budget_expenditure: parseNumericValue(updatedData.q1BudgetExpenditure),
          q1_authority_received: parseNumericValue(updatedData.q1AuthorityReceived),
          q1_expense: parseNumericValue(updatedData.q1Expense),
          q2_budget_demand: parseNumericValue(updatedData.q2BudgetDemand),
          q2_budget_expenditure: parseNumericValue(updatedData.q2BudgetExpenditure),
          q2_authority_received: parseNumericValue(updatedData.q2AuthorityReceived),
          q2_expense: parseNumericValue(updatedData.q2Expense),
          q3_budget_demand: parseNumericValue(updatedData.q3BudgetDemand),
          q3_budget_expenditure: parseNumericValue(updatedData.q3BudgetExpenditure),
          q3_authority_received: parseNumericValue(updatedData.q3AuthorityReceived),
          q3_expense: parseNumericValue(updatedData.q3Expense),
          q4_budget_demand: parseNumericValue(updatedData.q4BudgetDemand),
          q4_budget_expenditure: parseNumericValue(updatedData.q4BudgetExpenditure),
          q4_authority_received: parseNumericValue(updatedData.q4AuthorityReceived),
          q4_expense: parseNumericValue(updatedData.q4Expense),
          total_budget_demand: parseNumericValue(updatedData.totalBudgetDemand),
          total_budget_expenditure: parseNumericValue(updatedData.totalBudgetExpenditure),
          total_authority_received: parseNumericValue(updatedData.totalAuthorityReceived),
          total_expense: parseNumericValue(updatedData.totalExpense),
          total_remaining_balance: parseNumericValue(updatedData.totalRemainingBalance)
        })
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        showMessage('आइटम सफलतापूर्वक अपडेट भयो', 'success');
        // Refresh data from backend
        await fetchExistingData();
      } else {
        showMessage(`आइटम अपडेट गर्न समस्या भयो: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      showMessage('आइटम अपडेट गर्न समस्या भयो', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleItemDelete = async (itemId) => {
    if (!window.confirm('के तपाईं यो आइटम मेटाउन चाहनुहुन्छ?')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${EXPENDITURE_ITEMS_API}/${itemId}/delete/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        showMessage('आइटम सफलतापूर्वक मेटाइयो', 'success');
        // Refresh data from backend
        await fetchExistingData();
      } else {
        showMessage(`आइटम मेटाउन समस्या भयो: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      showMessage('आइटम मेटाउन समस्या भयो', 'error');
    } finally {
      setLoading(false);
    }
  };

  // API calls
  const fetchExistingData = useCallback(async () => {
    if (!selectedFiscalYear || !selectedOffice.id) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${GET_EXPENDITURE_API}?fiscal_year_id=${selectedFiscalYear.id}&institution_id=${selectedOffice.id}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        const { capital_items, operational_items, financial_data } = data.data;
        
        // Convert backend data to frontend format
        const convertedCapitalRows = capital_items.map((item, index) => ({
          id: item.id || index + 1,
          codeNumber: item.code_number || '',
          expenseTitle: item.expense_title || '',
          unallocatedBudget: item.unallocated_budget || '',
          revisedBudget: item.revised_budget || '',
          q1BudgetDemand: item.q1_budget_demand || '',
          q1BudgetExpenditure: item.q1_budget_expenditure || '',
          q1AuthorityReceived: item.q1_authority_received || '',
          q1Expense: item.q1_expense || '',
          q1RemainingBalance: item.q1_remaining_balance || '',
          q2BudgetDemand: item.q2_budget_demand || '',
          q2BudgetExpenditure: item.q2_budget_expenditure || '',
          q2AuthorityReceived: item.q2_authority_received || '',
          q2Expense: item.q2_expense || '',
          q2RemainingBalance: item.q2_remaining_balance || '',
          q3BudgetDemand: item.q3_budget_demand || '',
          q3BudgetExpenditure: item.q3_budget_expenditure || '',
          q3AuthorityReceived: item.q3_authority_received || '',
          q3Expense: item.q3_expense || '',
          q3RemainingBalance: item.q3_remaining_balance || '',
          q4BudgetDemand: item.q4_budget_demand || '',
          q4BudgetExpenditure: item.q4_budget_expenditure || '',
          q4AuthorityReceived: item.q4_authority_received || '',
          q4Expense: item.q4_expense || '',
          q4RemainingBalance: item.q4_remaining_balance || '',
          totalBudgetDemand: blankIfZero(item.total_budget_demand),
          totalBudgetExpenditure: blankIfZero(item.total_budget_expenditure),
          totalAuthorityReceived: blankIfZero(item.total_authority_received),
          totalExpense: blankIfZero(item.total_expense),
          totalRemainingBalance: blankIfZero(item.total_remaining_balance),
        }));
        
        const convertedOperationalRows = operational_items.map((item, index) => ({
          id: item.id || `op_${index + 1}`,
          codeNumber: item.code_number || '',
          expenseTitle: item.expense_title || '',
          unallocatedBudget: item.unallocated_budget || '',
          revisedBudget: item.revised_budget || '',
          q1BudgetDemand: item.q1_budget_demand || '',
          q1BudgetExpenditure: item.q1_budget_expenditure || '',
          q1AuthorityReceived: item.q1_authority_received || '',
          q1Expense: item.q1_expense || '',
          q1RemainingBalance: item.q1_remaining_balance || '',
          q2BudgetDemand: item.q2_budget_demand || '',
          q2BudgetExpenditure: item.q2_budget_expenditure || '',
          q2AuthorityReceived: item.q2_authority_received || '',
          q2Expense: item.q2_expense || '',
          q2RemainingBalance: item.q2_remaining_balance || '',
          q3BudgetDemand: item.q3_budget_demand || '',
          q3BudgetExpenditure: item.q3_budget_expenditure || '',
          q3AuthorityReceived: item.q3_authority_received || '',
          q3Expense: item.q3_expense || '',
          q3RemainingBalance: item.q3_remaining_balance || '',
          q4BudgetDemand: item.q4_budget_demand || '',
          q4BudgetExpenditure: item.q4_budget_expenditure || '',
          q4AuthorityReceived: item.q4_authority_received || '',
          q4Expense: item.q4_expense || '',
          q4RemainingBalance: item.q4_remaining_balance || '',
          totalBudgetDemand: blankIfZero(item.total_budget_demand),
          totalBudgetExpenditure: blankIfZero(item.total_budget_expenditure),
          totalAuthorityReceived: blankIfZero(item.total_authority_received),
          totalExpense: blankIfZero(item.total_expense),
          totalRemainingBalance: blankIfZero(item.total_remaining_balance),
        }));
        
        // Update state with fetched data
        if (convertedCapitalRows.length > 0) {
          setRows(convertedCapitalRows);
        }
        if (convertedOperationalRows.length > 0) {
          setOperationalRows(convertedOperationalRows);
        }

        setIncomeTotals(incomeStateFromApi(data.data));
        
        setExistingData(data.data);
        showMessage('डाटा सफलतापूर्वक लोड भयो', 'success');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showMessage('डाटा लोड गर्न समस्या भयो', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedFiscalYear, selectedOffice.id]);

  const fetchOfficeOptions = useCallback(async () => {
    try {
      const response = await fetch(INSTITUTIONS_API);
      const data = await response.json();
      if (data.status === 'success') {
        setOfficeOptions(data.data);
      }
    } catch (error) {
      console.error('Error fetching offices:', error);
    }
  }, []);

  const fetchFiscalYears = useCallback(async () => {
    try {
      const response = await fetch(FISCAL_YEARS_API, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.status === 'success') {
        const years = Array.isArray(data.data) ? data.data : [];
        setFiscalYears(years);
        setSelectedFiscalYear((prev) => keepOrDefaultFiscalYear(years, prev));
      }
    } catch (error) {
      console.error('Error fetching fiscal years:', error);
    }
  }, []);

  // Manual data loading function
  const handleLoadExistingData = async () => {
    if (!selectedOffice?.id || !selectedFiscalYear?.id) {
      showMessage('कृपया कार्यालय र आर्थिक वर्ष छान्नुहोस्', 'error');
      return;
    }
    await fetchExistingData();
  };

  const reportBundleRef = React.useRef({ key: null, data: null, inflight: null });

  // Fetch all saved data for data view tab
  const fetchAllSavedData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (selectedFiscalYear?.id) {
        params.append('fiscal_year_id', selectedFiscalYear.id);
      }
      const query = params.toString();
      const response = await fetch(
        query ? `${API_BASE_URL}/capital-expenditure/?${query}` : `${API_BASE_URL}/capital-expenditure/`,
        { headers }
      );
      const data = await response.json();

      if (data.status === 'success') {
        const safeData = Array.isArray(data.data) ? data.data : [];
        setExistingData(safeData);
        setFilteredData(safeData);
        reportBundleRef.current = { key: null, data: null, inflight: null };
        if (safeData.length > 0) {
          sessionStorage.removeItem('dataViewFetchAttempted');
        }
        showPopupMessage('सबै सेभ गरिएको डाटा लोड भयो', 'success');
      } else {
        showMessage('डाटा लोड गर्न समस्या भयो', 'error');
        setExistingData([]);
        setFilteredData([]);
      }
    } catch (error) {
      console.error('Error fetching all data:', error);
      showMessage('डाटा लोड गर्न समस्या भयो', 'error');
      setExistingData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedFiscalYear?.id]);

  // Function to fetch detailed data for reports
  const fetchDetailedDataForReports = useCallback(async () => {
    const yearId = selectedFiscalYear?.id;
    const cacheKey = String(yearId || 'all');

    if (reportBundleRef.current.key === cacheKey && Array.isArray(reportBundleRef.current.data)) {
      return reportBundleRef.current.data;
    }
    if (reportBundleRef.current.key === cacheKey && reportBundleRef.current.inflight) {
      return reportBundleRef.current.inflight;
    }

    const loadBundle = async () => {
      const params = new URLSearchParams();
      if (yearId) {
        params.append('fiscal_year_id', yearId);
      }
      params.append('include_items', '1');
      const response = await fetch(`${API_BASE_URL}/capital-expenditure/?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      const result = await response.json();
      if (result.status === 'success' && Array.isArray(result.data)) {
        return result.data;
      }
      return Array.isArray(existingData) ? existingData : [];
    };

    const inflight = loadBundle()
      .then((data) => {
        reportBundleRef.current = { key: cacheKey, data, inflight: null };
        return data;
      })
      .catch((error) => {
        console.error('Error fetching detailed data for reports:', error);
        reportBundleRef.current = { key: null, data: null, inflight: null };
        return Array.isArray(existingData) ? existingData : [];
      });

    reportBundleRef.current = { key: cacheKey, data: null, inflight };
    return inflight;
  }, [selectedFiscalYear?.id, existingData]);

  const fetchBudgetSubmissionDetail = async (budgetSubmissionId) => {
    if (!budgetSubmissionId) {
      return null;
    }

    try {
      const response = await fetch(`${BUDGET_LIST_API}${budgetSubmissionId}/`, {
        headers: getAuthHeaders()
      });
      const result = await response.json();
      if (result.status !== 'success' || !result.data) {
        return null;
      }
      return result.data;
    } catch (error) {
      console.error('Error fetching budget detail:', error);
      return null;
    }
  };

  const inferBudgetItemType = (item = {}) => {
    return inferBudgetItemTypeFromCode(item?.code_number, item?.item_type);
  };

  const parseBudgetAmount = (value) => {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const sortBudgetRowsByCode = (rows = []) => (
    sortBudgetRowsByFixedCode(rows, (row) => row?.code_number || '')
  );

  const buildBudgetTemplateRows = () => (
    buildFixedBudgetTemplateRows(masterItems).map((item) => ({
      code_number: item?.code_number || '',
      expense_title: item?.expense_title || '',
      estimated_budget: '',
      remarks: '',
      item_type: inferBudgetItemType(item)
    }))
  );

  const mergeBudgetRowsWithTemplate = (submissionRows = [], templateRows = []) => {
    const normalizedSubmissionRows = Array.isArray(submissionRows) ? submissionRows : [];
    const normalizedTemplateRows = Array.isArray(templateRows) ? templateRows : [];

    if (normalizedTemplateRows.length === 0) {
      return normalizedSubmissionRows;
    }

    const submissionByCode = new Map(
      normalizedSubmissionRows
        .filter((row) => String(row?.code_number || '').trim())
        .map((row) => [toCanonicalBudgetCode(row.code_number), row])
    );

    const mergedRows = normalizedTemplateRows.map((templateRow) => {
      const key = toCanonicalBudgetCode(templateRow.code_number);
      const matchedRow = key ? submissionByCode.get(key) : null;
      return {
        ...templateRow,
        estimated_budget: matchedRow?.estimated_budget ?? '',
        remarks: matchedRow?.remarks || ''
      };
    });

    const templateCodes = new Set(
      normalizedTemplateRows.map((row) => toCanonicalBudgetCode(row?.code_number || '')).filter(Boolean)
    );
    const customRows = normalizedSubmissionRows.filter((row) => {
      const key = toCanonicalBudgetCode(row?.code_number || '');
      return key ? !templateCodes.has(key) : (
        parseBudgetAmount(row?.estimated_budget) > 0 || String(row?.remarks || '').trim()
      );
    });

    return [...mergedRows, ...customRows];
  };

  const downloadBudgetSubmissionCsv = (budgetSubmission) => {
    const submissionRows = (budgetSubmission?.budget_items || []).map((item) => ({
      code_number: item?.code_number || '',
      expense_title: item?.expense_title || '',
      estimated_budget: item?.estimated_budget || '',
      remarks: item?.remarks || '',
      item_type: inferBudgetItemType(item)
    }));
    const mergedRows = mergeBudgetRowsWithTemplate(submissionRows, buildBudgetTemplateRows());
    const exportRows = mergedRows.length > 0 ? mergedRows : submissionRows;

    if (exportRows.length === 0) {
      showMessage('डाउनलोड गर्न बजेट डाटा फेला परेन', 'error');
      return;
    }

    const capitalRows = sortBudgetRowsByCode(
      exportRows.filter((row) => inferBudgetItemType(row) === 'capital')
    );
    const operationalRows = sortBudgetRowsByCode(
      exportRows.filter((row) => inferBudgetItemType(row) === 'operational')
    );
    const capitalTotal = capitalRows.reduce((sum, row) => sum + parseBudgetAmount(row?.estimated_budget), 0);
    const operationalTotal = operationalRows.reduce((sum, row) => sum + parseBudgetAmount(row?.estimated_budget), 0);
    const grandTotal = capitalTotal + operationalTotal;
    const toCsvBudgetValue = (value) => {
      const numericValue = parseBudgetAmount(value);
      return numericValue ? numericValue : '';
    };

    const rowsForCsv = [
      ['कोड नं.', 'खर्च शिर्षक', 'अनुमानित बजेट', 'कैफियत']
    ];

    rowsForCsv.push(['', 'पुँजीगत खर्च', '', '']);
    capitalRows.forEach((item) => {
      rowsForCsv.push([
        item?.code_number || '',
        item?.expense_title || '',
        toCsvBudgetValue(item?.estimated_budget),
        item?.remarks || ''
      ]);
    });
    rowsForCsv.push(['', 'जम्मा पुँजीगत', toCsvBudgetValue(capitalTotal), '']);

    rowsForCsv.push(['', 'संचालन खर्च', '', '']);
    operationalRows.forEach((item) => {
      rowsForCsv.push([
        item?.code_number || '',
        item?.expense_title || '',
        toCsvBudgetValue(item?.estimated_budget),
        item?.remarks || ''
      ]);
    });
    rowsForCsv.push(['', 'जम्मा संचालन', toCsvBudgetValue(operationalTotal), '']);
    rowsForCsv.push(['', 'जम्मा संचालन र पुँजीगत खर्च', toCsvBudgetValue(grandTotal), '']);

    const csvContent = `\uFEFF${rowsForCsv.map((row) => (
      row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')
    )).join('\n')}`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = fileUrl;

    const institutionSlug = String(budgetSubmission?.institution_name || 'institution')
      .replace(/\s+/g, '_')
      .replace(/[^\w-]/g, '');
    const fiscalYearSlug = String(budgetSubmission?.fiscal_year_display || 'fy')
      .replace(/\s+/g, '_')
      .replace(/[^\w-]/g, '');

    link.download = `budget_${institutionSlug}_${fiscalYearSlug}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(fileUrl);
  };

  const handleBudgetViewFromReport = async (budgetSubmissionId) => {
    const budgetSubmission = await fetchBudgetSubmissionDetail(budgetSubmissionId);
    if (!budgetSubmission) {
      showMessage('बजेट डाटा फेला परेन', 'error');
      return null;
    }

    const fallbackItems = Array.isArray(masterItems)
      ? masterItems.map((item) => ({
          code_number: item?.code_number || '',
          expense_title: item?.expense_title || '',
          item_type: item?.item_type || inferBudgetItemTypeFromCode(item?.code_number, item?.item_type)
        }))
      : [];

    return {
      budget_submission: budgetSubmission,
      fallback_items: fallbackItems
    };
  };

  const handleBudgetEditFromReport = async (budgetSubmissionId) => {
    const budgetSubmission = await fetchBudgetSubmissionDetail(budgetSubmissionId);
    if (!budgetSubmission) {
      showMessage('बजेट डाटा फेला परेन', 'error');
      return null;
    }

    const fallbackItems = Array.isArray(masterItems)
      ? masterItems.map((item) => ({
          code_number: item?.code_number || '',
          expense_title: item?.expense_title || '',
          item_type: item?.item_type || inferBudgetItemTypeFromCode(item?.code_number, item?.item_type)
        }))
      : [];

    return {
      budget_submission: budgetSubmission,
      fallback_items: fallbackItems
    };
  };

  const handleBudgetSaveFromReport = async ({ submission, rows }) => {
    if (!submission?.institution || !submission?.fiscal_year) {
      showMessage('संस्था वा आर्थिक वर्ष जानकारी भेटिएन', 'error');
      return null;
    }

    const payloadItems = (Array.isArray(rows) ? rows : [])
      .map((row) => ({
        code_number: String(row?.code_number || '').trim(),
        expense_title: String(row?.expense_title || '').trim(),
        estimated_budget: parseBudgetAmount(row?.estimated_budget),
        remarks: String(row?.remarks || '').trim()
      }))
      .filter((item) => (
        item.code_number ||
        item.expense_title ||
        item.estimated_budget > 0 ||
        item.remarks
      ));

    if (payloadItems.length === 0) {
      showMessage('सेभ गर्न कम्तिमा एउटा पंक्ति आवश्यक छ', 'error');
      return null;
    }

    try {
      const requestBody = {
        institution_id: submission.institution,
        fiscal_year_id: submission.fiscal_year,
        items: payloadItems
      };

      if (submission.token) {
        requestBody.token = submission.token;
      }

      const response = await fetch(SAVE_BUDGET_API, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });
      const result = await response.json();

      if (result.status !== 'success') {
        showMessage(result.message || 'बजेट सेभ गर्न समस्या भयो', 'error');
        return null;
      }

      const refreshedBudgetSubmission = await fetchBudgetSubmissionDetail(
        result?.data?.budget_submission_id || submission?.id
      );
      const savedBudgetSubmission = refreshedBudgetSubmission || {
        ...submission,
        token: result?.data?.token || submission?.token || '',
        budget_items: payloadItems
      };

      showMessage('बजेट डाटा सफलतापूर्वक सेभ भयो', 'success');
      return savedBudgetSubmission;
    } catch (error) {
      console.error('Error saving budget from report popup:', error);
      showMessage('बजेट सेभ गर्न समस्या भयो', 'error');
      return null;
    }
  };

  const handleBudgetDownloadFromReport = async (budgetSubmissionId) => {
    const budgetSubmission = await fetchBudgetSubmissionDetail(budgetSubmissionId);
    if (!budgetSubmission) {
      showMessage('बजेट डाटा डाउनलोड गर्न सकिएन', 'error');
      return;
    }

    downloadBudgetSubmissionCsv(budgetSubmission);
    showMessage('बजेट CSV डाउनलोड भयो', 'success');
  };

  const fetchBudgetReportData = useCallback(async (filterValues = {}) => {
    try {
      const params = new URLSearchParams();

      if (filterValues.institution) {
        params.append('institution', filterValues.institution);
      }
      if (filterValues.fiscalYear) {
        params.append('fiscal_year', filterValues.fiscalYear);
      } else if (selectedFiscalYear?.id) {
        params.append('fiscal_year_id', selectedFiscalYear.id);
      }
      if (filterValues.dateFrom) {
        params.append('date_from', filterValues.dateFrom);
      }
      if (filterValues.dateTo) {
        params.append('date_to', filterValues.dateTo);
      }
      if (filterValues.status) {
        params.append('status', filterValues.status);
      }

      const query = params.toString();
      const response = await fetch(
        query ? `${BUDGET_LIST_API}?${query}` : BUDGET_LIST_API,
        { headers: getAuthHeaders() }
      );
      const result = await response.json();

      if (result.status !== 'success' || !Array.isArray(result.data)) {
        return [];
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching budget report data:', error);
      return [];
    }
  }, [selectedFiscalYear?.id]);

  // Effects
  useEffect(() => {
    fetchOfficeOptions();
    fetchFiscalYears();
  }, [fetchOfficeOptions, fetchFiscalYears]);
  
  // Load data when switching to data view tab
  useEffect(() => {
    if (currentActiveTab === 'dataView' && selectedFiscalYear?.id) {
      const token = localStorage.getItem('authToken');
      if (token) {
        fetchAllSavedData();
      }
    } else if (currentActiveTab !== 'dataView') {
      sessionStorage.removeItem('dataViewFetchAttempted');
    }
  }, [currentActiveTab, selectedFiscalYear?.id, fetchAllSavedData]);
  
     // Clear form when switching to capital tab for new data entry
   useEffect(() => {
     // Only clear form when explicitly switching to capital tab for new data entry
     // Don't clear when loading data for editing
     if (currentActiveTab === 'capital' && viewMode === false && lastSavedToken === 'new') {
       // Check if we have data in the form that should be cleared
       const hasData = rows.some(row => 
         row.codeNumber.trim() || 
         row.expenseTitle.trim() || 
         parseNumericValue(row.unallocatedBudget) > 0 || 
         parseNumericValue(row.revisedBudget) > 0
       ) || operationalRows.some(row => 
         row.codeNumber.trim() || 
         row.expenseTitle.trim() || 
         parseNumericValue(row.unallocatedBudget) > 0 || 
         parseNumericValue(row.revisedBudget) > 0
       );
       
       if (hasData) {
         // Clear the form for new data entry
         setRows([{ 
           id: 1, 
           codeNumber: '', 
           expenseTitle: '', 
           unallocatedBudget: '', 
           revisedBudget: '', 
           q1BudgetDemand: '', 
           q1BudgetExpenditure: '', 
           q1AuthorityReceived: '', 
           q1Expense: '', 
           q1RemainingBalance: '',
           q2BudgetDemand: '', 
           q2BudgetExpenditure: '', 
           q2AuthorityReceived: '', 
           q2Expense: '', 
           q2RemainingBalance: '',
           q3BudgetDemand: '', 
           q3BudgetExpenditure: '', 
           q3AuthorityReceived: '', 
           q3Expense: '', 
           q3RemainingBalance: '',
           q4BudgetDemand: '', 
           q4BudgetExpenditure: '', 
           q4AuthorityReceived: '', 
           q4Expense: '', 
           q4RemainingBalance: '',
           totalBudgetDemand: '',
           totalBudgetExpenditure: '',
           totalAuthorityReceived: '',
           totalExpense: '',
           totalRemainingBalance: '',
         }]);
         
         setOperationalRows([{ 
           id: 'op_1', 
           codeNumber: '', 
           expenseTitle: '', 
           unallocatedBudget: '', 
           revisedBudget: '', 
           q1BudgetDemand: '', 
           q1BudgetExpenditure: '', 
           q1AuthorityReceived: '', 
           q1Expense: '', 
           q1RemainingBalance: '0',
           q2BudgetDemand: '', 
           q2BudgetExpenditure: '', 
           q2AuthorityReceived: '', 
           q2Expense: '', 
           q2RemainingBalance: '0',
           q3BudgetDemand: '', 
           q3BudgetExpenditure: '', 
           q3AuthorityReceived: '', 
           q3Expense: '', 
           q3RemainingBalance: '0',
           q4BudgetDemand: '', 
           q4BudgetExpenditure: '', 
           q4AuthorityReceived: '', 
           q4Expense: '', 
           q4RemainingBalance: '0',
           totalBudgetDemand: '',
           totalBudgetExpenditure: '',
           totalAuthorityReceived: '',
           totalExpense: '',
           totalRemainingBalance: '',
         }]);
         
         // Reset office and fiscal year selection
         setSelectedOffice({ id: null, name: '' });
         setSelectedFiscalYear(pickCurrentFiscalYear(fiscalYears));
         
         console.log('Form automatically cleared for new data entry');
       }
     }
      }, [currentActiveTab, viewMode, lastSavedToken, rows, operationalRows]);
   
   // Sync activeTab with current route
   useEffect(() => {
     const path = location.pathname;
     if (path.includes('/capital-expenditure/new-data') || path === '/capital-expenditure' || path === '/') {
       setActiveTab('capital');
     } else if (path.includes('/budjet')) {
       setActiveTab('budget');
     } else if (path.includes('/capital-expenditure/data-view')) {
       setActiveTab('dataView');
     } else if (path.includes('/capital-expenditure/institution')) {
       setActiveTab('institution');
     }
   }, [location.pathname]);
  
    /**
   * Render: नयाँ डाटा थप्नुहोस् (Add New Data) Complete Design
   * Includes: Header, Form with Table, Data View, and Institution Management
   */
  return (
    <div className={styles.capitalExpenditureContainer}>
      {/* Popup Message */}
      {showPopup && (
        <div className={styles.popupMessage}>
          <div className={`${styles.popupContent} ${styles[popupType]}`}>
            <span className={styles.popupText}>{popupMessage}</span>
            <button 
              className={styles.popupClose}
              onClick={() => setShowPopup(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Token Success Popup */}
      {showTokenPopup && (
        <div className={styles.tokenPopupOverlay}>
          <div className={styles.tokenPopup}>
            <div className={styles.tokenPopupHeader}>
              <div className={styles.tokenSuccessIcon}>✅</div>
              <h2>डाटा सफलतापूर्वक सेभ भयो!</h2>
              <p>तपाईंको डाटा सुरक्षित रूपमा सेभ गरियो। कृपया निम्न टोकन नोट गर्नुहोस्:</p>
            </div>
            
            <div className={styles.tokenDisplay}>
              <div className={styles.tokenLabel}>आपको टोकन:</div>
              <div className={styles.tokenValue}>{tokenData?.token}</div>
              <div className={styles.tokenInfo}>
                <div className={styles.tokenInfoItem}>
                  <span className={styles.tokenInfoLabel}>संस्था:</span>
                  <span className={styles.tokenInfoValue}>{tokenData?.institution}</span>
                </div>
                <div className={styles.tokenInfoItem}>
                  <span className={styles.tokenInfoLabel}>आर्थिक वर्ष:</span>
                  <span className={styles.tokenInfoValue}>{tokenData?.fiscalYear}</span>
                </div>
              </div>
            </div>
            
            <div className={styles.tokenPopupActions}>
              <button 
                className={styles.tokenCopyButton}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(tokenData?.token || '');
                    setCopySuccess(true);
                    showMessage('टोकन क्लिपबोर्डमा कपी गरियो!', 'success');
                    
                    // Reset copy success after 2 seconds
                    setTimeout(() => {
                      setCopySuccess(false);
                    }, 2000);
                  } catch (err) {
                    // Fallback for older browsers
                    const textArea = document.createElement('textarea');
                    textArea.value = tokenData?.token || '';
                    document.body.appendChild(textArea);
                    textArea.select();
                    try {
                      document.execCommand('copy');
                      setCopySuccess(true);
                      showMessage('टोकन क्लिपबोर्डमा कपी गरियो!', 'success');
                      
                      setTimeout(() => {
                        setCopySuccess(false);
                      }, 2000);
                    } catch (fallbackErr) {
                      showMessage('टोकन कपी गर्न सकिएन। कृपया म्यानुअल रूपमा कपी गर्नुहोस्।', 'error');
                    }
                    document.body.removeChild(textArea);
                  }
                }}
              >
                {copySuccess ? '✅ कपी भयो!' : '📋 टोकन कपी गर्नुहोस्'}
              </button>
              <button 
                className={styles.tokenCloseButton}
                onClick={handleCloseTokenPopup}
              >
                बुझेको छु
              </button>
            </div>
            
            <div className={styles.tokenPopupFooter}>
              <p>⚠️ यो टोकन सुरक्षित राख्नुहोस् - यसले तपाईंको डाटा हेर्न र सम्पादन गर्न सक्षम बनाउँछ</p>
            </div>
          </div>
        </div>
      )}

      {/* Refresh Confirmation Popup with Modal Overlay */}
      {showRefreshPopup && (
        <div className={styles.modalOverlay} onClick={cancelRefresh}>
          <div className={styles.popupMessage} onClick={(e) => e.stopPropagation()}>
            <div className={`${styles.popupContent} ${styles.refresh}`}>
              <div className={styles.refreshPopupHeader}>
                <h3>रिफ्रेश गर्ने?</h3>
                <button 
                  className={styles.popupClose}
                  onClick={cancelRefresh}
                >
                  ×
                </button>
              </div>
              <div className={styles.refreshPopupBody}>
                <p>तपाईं अन्य पृष्ठबाट फर्किएका हुनुहुन्छ। के तपाईं पृष्ठ रिफ्रेश गर्न चाहनुहुन्छ?</p>
                <div className={styles.refreshPopupActions}>
                  <button 
                    className={styles.confirmButton}
                    onClick={confirmRefresh}
                  >
                    हो, रिफ्रेश गर्नुहोस्
                  </button>
                  <button 
                    className={styles.cancelButton}
                    onClick={cancelRefresh}
                  >
                    होइन, रद्द गर्नुहोस्
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Header Component */}
      <CapitalExpenditureHeader
        activeTab={currentActiveTab}
        setActiveTab={setActiveTab}
        selectedOffice={selectedOffice}
        setSelectedOffice={setSelectedOffice}
        selectedFiscalYear={selectedFiscalYear}
        setSelectedFiscalYear={setSelectedFiscalYear}
        fiscalYears={fiscalYears}
        viewMode={viewMode}
        showOfficeDropdown={showOfficeDropdown}
        officeSearchTerm={officeSearchTerm}
        officeOptions={officeOptions}
        handleOfficeDropdownToggle={handleOfficeDropdownToggle}
        handleOfficeSelect={handleOfficeSelect}
        setOfficeSearchTerm={setOfficeSearchTerm}
        lastSavedToken={lastSavedToken}
        message={message}
        messageType={messageType}
        onRefreshClick={handleRefreshClick}
        onLogout={onLogout}
        isAuthenticated={isAuthenticated}
        onTabChange={(tabName) => {
          // Clear edit form and main form state when switching tabs
          setShowEditForm(false);
          setEditFormData(null);
          clearMainFormState();
          setViewMode(false);
          setLastSavedToken('new');
          
          // Navigate to the appropriate route
          if (tabName === 'newData') {
            navigate('/capital-expenditure/new-data');
            
            // Check if returning from other page and show refresh popup
            if (hasReturnedFromOtherPage) {
              setShowRefreshPopup(true);
            }
            
            // Reset form to original state for new data entry - exactly like first visit
            setRows(Array(4).fill().map((_, index) => ({
              id: index + 1,
              codeNumber: '', 
              expenseTitle: '', 
              unallocatedBudget: '', 
              revisedBudget: '',
              q1BudgetDemand: '', 
              q1BudgetExpenditure: '', 
              q1AuthorityReceived: '', 
              q1Expense: '', 
              q1RemainingBalance: '0',
              q2BudgetDemand: '', 
              q2BudgetExpenditure: '', 
              q2AuthorityReceived: '', 
              q2Expense: '', 
              q2RemainingBalance: '0',
              q3BudgetDemand: '', 
              q3BudgetExpenditure: '', 
              q3AuthorityReceived: '', 
              q3Expense: '', 
              q3RemainingBalance: '0',
              q4BudgetDemand: '', 
              q4BudgetExpenditure: '', 
              q4AuthorityReceived: '', 
              q4Expense: '', 
              q4RemainingBalance: '0',
              totalBudgetDemand: '',
              totalBudgetExpenditure: '',
              totalAuthorityReceived: '',
              totalExpense: '',
              totalRemainingBalance: '',
              is_uploaded: false
            })));
            
            setOperationalRows(Array(4).fill().map((_, index) => ({
              id: `op_${index + 1}`,
              codeNumber: '', 
              expenseTitle: '', 
              unallocatedBudget: '', 
              revisedBudget: '',
              q1BudgetDemand: '', 
              q1BudgetExpenditure: '', 
              q1AuthorityReceived: '', 
              q1Expense: '', 
              q1RemainingBalance: '0',
              q2BudgetDemand: '', 
              q2BudgetExpenditure: '', 
              q2AuthorityReceived: '', 
              q2Expense: '', 
              q2RemainingBalance: '0',
              q3BudgetDemand: '', 
              q3BudgetExpenditure: '', 
              q3AuthorityReceived: '', 
              q3Expense: '', 
              q3RemainingBalance: '0',
              q4BudgetDemand: '', 
              q4BudgetExpenditure: '', 
              q4AuthorityReceived: '', 
              q4Expense: '', 
              q4RemainingBalance: '0',
              totalBudgetDemand: '',
              totalBudgetExpenditure: '',
              totalAuthorityReceived: '',
              totalExpense: '',
              totalRemainingBalance: '',
              is_uploaded: false
            })));
            
            // Reset office and fiscal year selection
            setSelectedOffice({ id: null, name: '' });
            setSelectedFiscalYear(pickCurrentFiscalYear(fiscalYears));
          } else if (tabName === 'budget') {
            navigate('/budjet');
            setHasReturnedFromOtherPage(true);
          } else if (tabName === 'dataView') {
            navigate('/capital-expenditure/data-view');
            setHasReturnedFromOtherPage(true);
          } else if (tabName === 'institutionManager') {
            navigate('/capital-expenditure/institution-manager');
            setHasReturnedFromOtherPage(true);
          } else if (tabName === 'tokenSearch') {
            navigate('/capital-expenditure/token-search');
            setHasReturnedFromOtherPage(true);
          } else if (tabName === 'settings') {
            navigate('/capital-expenditure/settings');
            setHasReturnedFromOtherPage(true);
          }
        }}
      />

      {/* Tab Content - Conditional Rendering */}
      {currentActiveTab === 'newData' && (
        <CapitalExpenditureForm
          rows={rows}
          operationalRows={operationalRows}
          viewMode={viewMode}
          handleInputChange={handleInputChange}
          handleOperationalRowChange={handleOperationalInputChange}
          addRow={addRow}
          addOperationalRow={addOperationalRow}
          removeRow={removeRow}
          handleSubmit={handleSubmit}
          handleReset={handleReset}
          handleClearForNewData={handleClearForNewData}
          handleDownloadFormData={handleDownloadFormData}
          selectedFiscalYear={selectedFiscalYear}
          setSelectedFiscalYear={setSelectedFiscalYear}
          fiscalYears={fiscalYears}
          loading={loading}
          onItemUpdate={handleItemUpdate}
          onItemDelete={handleItemDelete}
          onLoadExistingData={handleLoadExistingData}
          selectedOffice={selectedOffice}
          isAuthenticated={isAuthenticated}
          incomeTotals={incomeTotals}
          onIncomeChange={handleIncomeChange}
          onToggleViewMode={() => {
            if (viewMode) {
              // Switching from view to edit mode - clear form and allow editing
              clearMainFormState();
              setViewMode(false);
            } else {
              // Switching from edit to view mode - keep current data in view mode
              setViewMode(true);
            }
          }}
        />
      )}
      
      {currentActiveTab === 'dataView' && (
        <CapitalExpenditureDataView
          existingData={existingData}
          filteredData={filteredData}
          selectedDataForEdit={selectedDataForEdit}
          viewMode={false}
          loading={loading}
          showFilters={showFilters}
          filters={filters}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleUpdate={handleUpdate}
          handleExportExcel={handleExportExcel}
          handleExportFilteredData={handleExportFilteredData}
          handlePrint={handlePrint}
          setShowFilters={setShowFilters}
          setFilters={setFilters}
          setSelectedDataForEdit={setSelectedDataForEdit}
          setViewMode={() => {}}
          onRefreshData={fetchAllSavedData}
          setFilteredData={setFilteredData}
          selectedOffice={selectedOffice}
          fiscalYears={fiscalYears}
          selectedFiscalYear={selectedFiscalYear}
          masterItems={masterItems}
          fetchDetailedDataForReports={fetchDetailedDataForReports}
          fetchBudgetReportData={fetchBudgetReportData}
          handleBudgetView={handleBudgetViewFromReport}
          handleBudgetEdit={handleBudgetEditFromReport}
          handleBudgetDownload={handleBudgetDownloadFromReport}
          handleBudgetSave={handleBudgetSaveFromReport}
        />
      )}
      
      {currentActiveTab === 'institutionManager' && (
        <InstitutionManager />
      )}
      
      {currentActiveTab === 'settings' && (
        <Settings
          onUploadItems={handleUploadItems}
          uploading={uploading}
          selectedOffice={selectedOffice}
          selectedFiscalYear={selectedFiscalYear}
        />
      )}

      {/* Token Search Tab Content - Accessible without authentication */}

       
       {/* Edit Form Modal */}
       {console.log('Edit form render condition:', { showEditForm, editFormData: !!editFormData, viewMode })}
       {showEditForm && editFormData && (
         <CapitalExpenditureEditForm
           expenditureData={editFormData}
           onSave={handleEditFormSave}
           onCancel={handleEditFormCancel}
           loading={loading || tokenYearSwitchLoading}
           viewMode={viewMode}
           fiscalYears={
             editFormData.available_fiscal_years?.length
               ? editFormData.available_fiscal_years
               : fiscalYears
           }
           onFiscalYearChange={editFormData.source_token ? handleTokenFormYearChange : undefined}
           yearSwitchLoading={tokenYearSwitchLoading}
         />
       )}
     </div>
   );
 };

export default CapitalExpenditureMain;
